"use client";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import { NumericFormat } from "react-number-format";
import AlertDialog from "./AlertDialog";
import { FiTrash } from "react-icons/fi";

import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "13px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "13px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    border: "1px solid #4b5563",
    zIndex: 50,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "&:hover": {
      backgroundColor: "#f97316",
      color: "#fff",
    },
  }),
};

const calculateCoverageFromSize = (size, uom) => {
  if (!size) return 1;
  const uomLower = (uom || "").toLowerCase();
  const isSF =
    uomLower.includes("sf") ||
    uomLower.includes("square") ||
    uomLower.includes("sq");
  const isLF =
    uomLower === "lf" ||
    uomLower.includes("linear") ||
    uomLower.includes("lin ft") ||
    uomLower.includes("lin.ft") ||
    uomLower.includes("lin");

  // Non-area/linear UOMs: coverage is always 1
  if (!isSF && !isLF) return 1;

  const parseToFeet = (str) => {
    const s = String(str).trim();
    const compactFeetInchMatch = s.match(/^([\d.]+)'([\d.]+)"?$/);
    if (compactFeetInchMatch) {
      return (
        parseFloat(compactFeetInchMatch[1]) +
        (parseFloat(compactFeetInchMatch[2]) || 0) / 12
      );
    }
    const spacedFeetInchMatch = s.match(/^([\d.]+)'?\s+([\d.]+)"?$/);
    if (spacedFeetInchMatch) {
      return (
        parseFloat(spacedFeetInchMatch[1]) +
        (parseFloat(spacedFeetInchMatch[2]) || 0) / 12
      );
    }
    if (s.endsWith("'")) return parseFloat(s.slice(0, -1)) || 0;
    if (s.endsWith('"')) return (parseFloat(s.slice(0, -1)) || 0) / 12;
    return parseFloat(s) || 0;
  };

  if (isSF) {
    const parts = String(size)
      .replace(/×/g, "x")
      .split(/\s*[xX]\s*/);
    if (parts.length === 2) {
      const width = parseToFeet(parts[0].trim());
      const height = parseToFeet(parts[1].trim());
      if (width > 0 && height > 0) {
        return Number((width * height).toFixed(2));
      }
    }
  }

  if (isLF) {
    const length = parseToFeet(size);
    if (length > 0) return Number(length.toFixed(2));
  }

  return 1;
};

const isValidLaborId = (laborId) => {
  if (!laborId || laborId === "-" || laborId === "") return false;
  return /^L[1-5]$/i.test(String(laborId).trim());
};

const getCoverage = (item) => {
  if (!item) return 1;

  const uomLower = (item.uom || "").toLowerCase();
  const isCoverageUom =
    uomLower.includes("sf") ||
    uomLower.includes("square") ||
    uomLower.includes("sq") ||
    uomLower === "lf" ||
    uomLower.includes("linear") ||
    uomLower.includes("lin ft") ||
    uomLower.includes("lin.ft") ||
    uomLower.includes("lin");

  // Non-coverage UOMs always have coverage of 1
  if (!isCoverageUom) return 1;

  const origSize = item.originalSize || "";
  const currentSize = item.size || "";
  const sizeChanged = currentSize.trim() !== origSize.trim();

  if (sizeChanged && currentSize) {
    const calculated = calculateCoverageFromSize(currentSize, item.uom);
    if (calculated > 0) return calculated;
  }

  if (
    item.coveragePerUnit !== undefined &&
    item.coveragePerUnit !== null &&
    item.coveragePerUnit !== ""
  ) {
    const parsed = parseFloat(item.coveragePerUnit);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return 1;
};

const safeNumber = (val, defaultVal = 0) => {
  if (val === undefined || val === null || val === "") return defaultVal;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? defaultVal : parsed;
};

const calculateLineTotal = (item) => {
  if (!item) return 0;
  const coverage = getCoverage(item);
  const qty = safeNumber(item.quantity, 0);
  const price = safeNumber(item.price, 0);
  const total = qty * coverage * price;
  return isNaN(total) ? 0 : total;
};

export default function EditMaterialOrderView() {
  const { id, orderId } = useParams();
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole");

  const [vendorPdfBuffer, setVendorPdfBuffer] = useState(null);
  const [sitePdfBuffer, setSitePdfBuffer] = useState(null);

  const [order, setOrder] = useState(null);
  const [project, setProject] = useState(null);
  const [po, setPO] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [linkedLaborItems, setLinkedLaborItems] = useState([]);
  const [itemsVersion, setItemsVersion] = useState(0);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: `Dear Vendor,\n\nPlease find attached the material order for your review and action.\n\nBest regards,`,
    signature: "",
  });
  const [pdfBuffer, setPdfBuffer] = useState(null);
  // Add after existing state declarations
  const [showForemanChangesModal, setShowForemanChangesModal] = useState(false);
  const [foremanSizeChanges, setForemanSizeChanges] = useState([]);
  const [availablePOItems, setAvailablePOItems] = useState([]);
  const [showAddFromPO, setShowAddFromPO] = useState(false);
  const [addItemSearch, setAddItemSearch] = useState("");
  const [availableBudgetItems, setAvailableBudgetItems] = useState([]);
  const [showStockOutModal, setShowStockOutModal] = useState(false);
  const [stockOutVendors, setStockOutVendors] = useState([]);
  const [stockOutSelectedVendor, setStockOutSelectedVendor] = useState(null);
  const [stockOutMissing, setStockOutMissing] = useState([]);
  const [stockOutReason, setStockOutReason] = useState("");
  const [isLoadingStockOut, setIsLoadingStockOut] = useState(false);
  const [showAddNewItem, setShowAddNewItem] = useState(false);
  const [newItemSearch, setNewItemSearch] = useState("");
  const [laborVariances, setLaborVariances] = useState([]);
  const [stockOutReasonCategory, setStockOutReasonCategory] = useState("");
  const [stockOutReasonText, setStockOutReasonText] = useState("");
  // Store the full net quantities data from the project-wide endpoint
  const [projectNetQuantities, setProjectNetQuantities] = useState(new Map());

  const [laborDeltaOverrides, setLaborDeltaOverrides] = useState({});

  const [editableItems, setEditableItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [surcharges, setSurcharges] = useState([]);
  const [laborRateOverrides, setLaborRateOverrides] = useState({});

  // ============================================================
  // ADD THESE STATE DECLARATIONS (near other state declarations)
  // ============================================================

  const multiSelectFilterStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#1f2937",
      borderColor: state.isFocused ? "#f97316" : "#4b5563",
      color: "#e5e7eb",
      boxShadow: "none",
      minHeight: "50px",
      zIndex: 100000,
      fontSize: "14px",
      "&:hover": { borderColor: "#f97316" },
    }),
    input: (provided) => ({ ...provided, color: "#e5e7eb" }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
      fontSize: "14px",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#374151",
      borderRadius: "0.5rem",
      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
      border: "1px solid #4b5563",
      width: "300px",
      maxWidth: "300px",
      minWidth: "260px",
      zIndex: 999999,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "380px",
      padding: "8px",
      overflowY: "auto",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#4b5563" : "#374151",
      color: state.isSelected ? "#f97316" : "#e5e7eb",
      padding: "10px 12px",
      cursor: "pointer",
      fontSize: "13.5px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.4",
      "&:hover": { backgroundColor: "#4b5563" },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "4px 8px",
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      alignItems: "flex-start",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#4b5563",
      borderRadius: "6px",
      margin: 0,
      fontSize: "13px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#e5e7eb",
      padding: "3px 8px",
      paddingRight: "6px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "150px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#9ca3af",
      cursor: "pointer",
      paddingLeft: "4px",
      paddingRight: "4px",
      borderRadius: "4px",
      ":hover": { backgroundColor: "#f97316", color: "white" },
    }),
  };

  const [groupByAssembly, setGroupByAssembly] = useState(false);
  const [tableFilters, setTableFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    bidArea: [],
    uom: [],
    size: [], // ADD THIS
    descriptionSearch: "",
  });

  const tableSizeRef = useRef(null);
  const [showTableFilter, setShowTableFilter] = useState(null);

  // Refs for filter dropdowns
  const tableConditionRef = useRef(null);
  const tableSectionRef = useRef(null);
  const tableBidAreaRef = useRef(null);
  const tableUomRef = useRef(null);

  const [formData, setFormData] = useState({
    shippingMethod: "Delivery",
    deliveryInstructions: "",
    expectedDeliveryDate: "",
    laborersRequired: false,
    laborersQty: 0,
    contact: [],
    fieldContact: [],
    deliveryContact: null,
    paid: false,
    paidDate: "",
    revision: 0,
    varianceReason: "",
    varianceReasonCategory: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showVariancePanel, setShowVariancePanel] = useState(true);
  const [userEmail, setUserEmail] = useState("");

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: "Close",
    onConfirm: () => {},
    onClose: () => setAlert((prev) => ({ ...prev, open: false })),
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showTableFilter) return;

      const refs = {
        conditionName: tableConditionRef,
        sectionNumber: tableSectionRef,
        bidArea: tableBidAreaRef,
        uom: tableUomRef,
        size: tableSizeRef, // ADD THIS
      };

      const activeRef = refs[showTableFilter];
      if (activeRef?.current?.contains(event.target)) return;

      const isReactSelectClick =
        event.target.closest('[class*="css-"]') ||
        event.target.closest(".react-select__menu") ||
        event.target.closest(".react-select__menu-portal") ||
        event.target.closest(".react-select__option") ||
        event.target.id?.startsWith("react-select");

      if (isReactSelectClick) return;

      setShowTableFilter(null);
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showTableFilter]);
  /**
   * Get net quantity data for a line item from the project-wide net quantities
   * This accounts for all orders across the project, not just this material order
   */
  const getNetItemData = useCallback(
    (lineItemId) => {
      if (!lineItemId) return null;
      const targetId = lineItemId.toString();
      const match = projectNetQuantities.get(targetId);
      if (!match) return null;
      return {
        netRequiredQty: match.netRequiredQty,
        qtyOrdered: match.qtyOrdered,
        qtyPending: match.qtyPending,
        netRemaining: match.netRemaining,
        baseRequiredQty: match.baseRequiredQty,
        deltaQty: match.deltaQty,
        hasVariance: match.hasVariance,
        // Include latest price/size info
        latestPrice: match.latestPrice,
        latestSize: match.latestSize,
        latestCoveragePerUnit: match.latestCoveragePerUnit,
        hasPriceChange: match.hasPriceChange,
      };
    },
    [projectNetQuantities],
  );
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const decoded = jwtDecode(token);
        setUserEmail(decoded.email || "");
        const headers = { Authorization: `Bearer ${token}` };

        // First fetch the order and project to get projectId
        const [orderRes, projectRes] = await Promise.all([
          axios.get(`${API_BASE}/materialOrders2/${orderId}`, { headers }),
          axios.get(`${API_BASE}/projects/${id}`, { headers }),
        ]);

        const orderData = orderRes.data;
        const projectData = projectRes.data;

        // Now fetch the project-wide net quantities from the correct endpoint
        const [netQtyRes, vendorsRes, contactsRes, usersRes, companyRes] =
          await Promise.all([
            axios.get(
              `${API_BASE}/net-assemblies/project-by-source/${projectData._id}`,
              {
                headers,
                params: { category: "M" }, // Only material items
              },
            ),
            axios.get(`${API_BASE}/accounts/vendors`, { headers }),
            axios.get(`${API_BASE}/contacts`, { headers }),
            axios.get(`${API_BASE}/users`, { headers }),
            axios.get(`${API_BASE}/company-profile`, { headers }),
          ]);

        // ═══════════════════════════════════════════════════════════════════
        // FIX: Build net quantities map with CORRECT aggregation logic
        // For price changes, we need to:
        // 1. Use baseRequiredQty (not sum of split rows' netRequiredQty)
        // 2. Sum qtyOrdered across all rows (historical + new)
        // 3. Use netRemaining from the NON-historical row
        // ═══════════════════════════════════════════════════════════════════
        // ═══════════════════════════════════════════════════════════════════
        // FIX: Build net quantities map from project-by-source response
        // The project-by-source endpoint returns { originalItems, changeOrderItems }
        // NOT { data: [...] } like the /project/:projectId endpoint
        // ═══════════════════════════════════════════════════════════════════
        const netQuantitiesMap = new Map();

        // Combine originalItems and changeOrderItems into a single array
        const allNetItems = [
          ...(netQtyRes.data.originalItems || []),
          ...(netQtyRes.data.changeOrderItems || []),
        ];

        if (allNetItems.length > 0) {
          // First pass: group all rows by original line item ID
          const groupedByKey = new Map();

          allNetItems.forEach((item) => {
            const key = (
              item.originalLineItemId ||
              item.key ||
              item.lineItemId ||
              item._id
            ).toString();
            const altKey = (item.lineItemId || item._id || "").toString();
            if (!groupedByKey.has(key)) groupedByKey.set(key, []);
            groupedByKey.get(key).push(item);

            if (altKey && altKey !== key) {
              if (!groupedByKey.has(altKey)) groupedByKey.set(altKey, []);
              groupedByKey.get(altKey).push(item);
            }
          });

          // Second pass: process each group to get correct aggregated values
          groupedByKey.forEach((items, key) => {
            const hasPriceChange =
              items.length > 1 ||
              items.some((i) => i.hasPriceChange || i.isPriceChangeRow);

            const currentRow =
              items.find((i) => !i.isHistoricalPriceLine) || items[0];
            const historicalRow = items.find((i) => i.isHistoricalPriceLine);

            let totalQtyOrdered = 0;
            let totalQtyPending = 0;

            items.forEach((item) => {
              totalQtyOrdered += item.qtyOrdered || 0;
              totalQtyPending += item.qtyPending || 0;
            });

            let netRequiredQty;
            if (hasPriceChange && items.length > 1) {
              netRequiredQty = items.reduce(
                (sum, item) => sum + (item.netRequiredQty || 0),
                0,
              );
            } else {
              netRequiredQty = currentRow.netRequiredQty || 0;
            }

            // Store RAW qtyOrdered and qtyPending from the API
            // The "exclude this order" logic happens in the item builder below
            netQuantitiesMap.set(key, {
              netRequiredQty,
              qtyOrdered: totalQtyOrdered,
              qtyPending: totalQtyPending,
              // netRemaining calculated raw (includes ALL pending)
              netRemaining: Math.max(
                0,
                netRequiredQty - totalQtyOrdered - totalQtyPending,
              ),
              baseRequiredQty: currentRow.baseRequiredQty || netRequiredQty,
              deltaQty:
                currentRow.varianceTotalDeltaQty || currentRow.deltaQty || 0,
              hasVariance: currentRow.hasVariance || hasPriceChange,
              latestSize: currentRow.latestSize || currentRow.size || "",
              latestPrice: currentRow.latestPrice ?? currentRow.price ?? 0,
              latestCoveragePerUnit:
                currentRow.latestCoveragePerUnit ||
                currentRow.coveragePerUnit ||
                "",
              hasPriceChange,
              priceChangedFrom:
                historicalRow?.price || currentRow.priceChangedFrom || null,
              priceChangedTo:
                currentRow.latestPrice ?? currentRow.price ?? null,
            });

            const lineItemId = (
              items[0]?.lineItemId ||
              items[0]?._id ||
              ""
            ).toString();
            if (lineItemId && lineItemId !== key) {
              netQuantitiesMap.set(lineItemId, netQuantitiesMap.get(key));
            }
          });
        }

        setProjectNetQuantities(netQuantitiesMap);

        setOrder(orderData);

        if (
          orderData.createdByForeman &&
          orderData.foremanSizeChanges?.length > 0
        ) {
          setForemanSizeChanges(orderData.foremanSizeChanges);
          setShowForemanChangesModal(true);
        }

        // Also detect any items where size differs from original
        const detectedSizeChanges = [];
        orderData.items.forEach((item) => {
          if (
            item.sizeChangedByForeman ||
            (item.originalSize && item.size !== item.originalSize)
          ) {
            const lineItem = item.lineItem || {};
            detectedSizeChanges.push({
              conditionName:
                item.conditionName || lineItem.conditionName || "Unknown",
              materialCode: lineItem.materialCode || "N/A",
              originalSize: item.originalSize || lineItem.size || "-",
              newSize: item.size || "-",
              quantity: item.quantity || 0,
              laborId: item.laborId || lineItem.laborId || null,
            });
          }
        });

        if (
          detectedSizeChanges.length > 0 &&
          !orderData.foremanSizeChanges?.length
        ) {
          setForemanSizeChanges(detectedSizeChanges);
          setShowForemanChangesModal(true);
        }
        setProject(projectData);
        setVendors(vendorsRes.data);
        setContacts(contactsRes.data);
        setUsers(usersRes.data);
        setCompanyProfile(companyRes.data);
        setLinkedLaborItems(orderData.linkedLaborItems || []);

        // Initialize email form
        const contactEmails = contactsRes.data
          .filter((contact) =>
            (Array.isArray(orderData.contact)
              ? orderData.contact.map((c) => c._id || c)
              : [orderData.contact?._id || orderData.contact]
            ).includes(contact._id),
          )
          .map((contact) => contact.email)
          .filter((email) => email)
          .join(", ");

        const primaryContact = contactsRes.data.find((contact) =>
          (Array.isArray(orderData.contact)
            ? orderData.contact.map((c) => c._id || c)
            : [orderData.contact?._id || orderData.contact]
          ).includes(contact._id),
        );

        const greeting = primaryContact?.firstName
          ? `Hi ${primaryContact.firstName},`
          : `Dear Vendor,`;

        const currentUser = usersRes.data.find(
          (u) => u.email === decoded.email,
        );

        setEmailForm({
          to: contactEmails || "",
          subject: `${projectData.projectName} - PO#${projectData.projectNumber}-${orderData.materialOrderNumber}`,
          body: `${greeting}\n\nPlease find attached the material order ${orderData.materialOrderNumber} for your review and action.\n\nBest regards,`,
          signature:
            currentUser?.displayName ||
            decoded.displayName ||
            decoded.name ||
            decoded.email ||
            "User",
        });

        setFormData({
          shippingMethod: orderData.shippingMethod || "Delivery",
          deliveryInstructions: orderData.deliveryInstructions || "",
          expectedDeliveryDate: orderData.expectedDeliveryDate
            ? orderData.expectedDeliveryDate.split("T")[0]
            : "",
          laborersRequired: orderData.laborersRequired || false,
          laborersQty: orderData.laborersQty || 0,
          contact: Array.isArray(orderData.contact)
            ? orderData.contact.map((c) => c._id || c)
            : [],
          fieldContact: Array.isArray(orderData.fieldContact)
            ? orderData.fieldContact.map((u) => u._id || u)
            : [],
          deliveryContact:
            orderData.deliveryContact?._id || orderData.deliveryContact || null,
          paid: orderData.paid || false,
          paidDate: orderData.paidDate ? orderData.paidDate.split("T")[0] : "",
          revision: orderData.revision || 0,
          varianceReason: orderData.varianceReason || "",
          varianceReasonCategory: orderData.varianceReasonCategory || "",
        });

        setSurcharges(orderData.surcharges || []);

        const poId = orderData.purchaseOrder?._id || orderData.purchaseOrder;

        // Build items with proper net quantity data
        // IMPORTANT: This order's items are included in the API's qtyPending totals.
        // We must use the ORIGINAL quantity saved on the server (not the edited value)
        // to correctly subtract this order's contribution to pending.
        const items = orderData.items.map((item) => {
          const lineItem = item.lineItem || {};
          const description =
            item.costCodeDescription ||
            item.description ||
            lineItem.costCodeDescription ||
            lineItem.itemDescription ||
            "";
          const uom =
            item.uom ||
            item.container ||
            lineItem.uom ||
            lineItem.container ||
            "";

          const itemLinkedPO = item.linkedPO || lineItem.linkedPO?.toString();
          const isFromPO = itemLinkedPO && itemLinkedPO === poId?.toString();

          // Use originalLineItemId for net lookup if present
          const lookupId = item.originalLineItemId || item.lineItemId;
          const lookupIdStr = String(lookupId);
          const netData = netQuantitiesMap.get(lookupIdStr);

          const budgetedQty =
            item.budgetedQty || lineItem.requiredQty || lineItem.quantity || 0;

          const netRequiredQty = netData?.netRequiredQty ?? budgetedQty;
          const qtyOrdered = netData?.qtyOrdered ?? 0;
          const qtyPending = netData?.qtyPending ?? 0;

          // ═══════════════════════════════════════════════════════════════════
          // FIX: Use the SAVED quantity on the server (item.quantity from the
          // database) as "this order's contribution to pending". This is what
          // the API counted when computing qtyPending. We must NOT use the
          // user's in-progress edits here — those haven't been saved yet.
          // ═══════════════════════════════════════════════════════════════════
          const thisOrderSavedQty = item.quantity || 0;
          const otherPending = Math.max(0, qtyPending - thisOrderSavedQty);

          // remainingAvailable = what's available for THIS order to claim
          // = total budget - already delivered - other orders' pending
          const remainingAvailable = Math.max(
            0,
            netRequiredQty - qtyOrdered - otherPending,
          );

          // Variance/new items = not from PO
          const isVarianceOrNewItem = !isFromPO;

          // Max quantity: unlimited for variance items, otherwise remaining available
          const maxQty = isVarianceOrNewItem ? Infinity : remainingAvailable;

          const originalQuantity = isFromPO ? item.quantity || 0 : 0;

          // Use latestSize/latestPrice from net data if available
          const effectiveSize = netData?.latestSize || lineItem.size || "";
          const effectivePrice =
            netData?.latestPrice ?? lineItem.price ?? lineItem.cost ?? 0;

          const originalSize = isFromPO ? effectiveSize : "";
          const originalPrice = isFromPO ? effectivePrice : 0;

          const isClosedOut = lineItem.closedOutBySizeChange === true;

          const originalCoverage = getCoverage({
            ...item,
            size: originalSize,
            uom,
          });

          // Commitment tracking excludes this order entirely
          const totalCommitted = qtyOrdered + otherPending;
          const projectedOverage = Math.max(0, totalCommitted - netRequiredQty);
          const isOverCommitted = totalCommitted > netRequiredQty;

          return {
            assembly: item.assembly?._id || item.assembly,
            lineItemId: item.lineItemId,
            originalLineItemId: item.originalLineItemId || null,
            quantity: item.quantity || 0,
            size: item.size || lineItem.size || "",
            price: item.price || lineItem.price || lineItem.cost || 0,
            bidArea: item.bidArea || lineItem.bidArea || "",
            budgetedQty,
            isClosedOut,
            netRequiredQty,
            qtyOrdered,
            qtyPending,
            otherPending,
            remainingAvailable,
            maxQty,
            showMaxAsUnlimited: isVarianceOrNewItem,
            conditionName: item.conditionName || "",
            description,
            uom,
            laborId: item.laborId || null,
            coveragePerUnit:
              item.coveragePerUnit || lineItem.coveragePerUnit || "",
            materialCode: item.materialCode || lineItem.materialCode || "",
            sectionNumber: item.sectionNumber || lineItem.sectionNumber || "",
            isAddedFromPO: false,
            isVarianceItem: !isFromPO,
            originalQuantity,
            originalSize,
            originalPrice,
            originalCoverage,
            currentCoverage: getCoverage(item),
            // Store the saved qty so variance calculations can use it
            _serverSavedQty: thisOrderSavedQty,
            totalCommitted,
            projectedOverage,
            isOverCommitted,
            hasPriorPriceChange: netData?.hasPriceChange || false,
            priorPriceChangedFrom: netData?.priceChangedFrom || null,
            priorPriceChangedTo: netData?.priceChangedTo || null,
          };
        });

        setEditableItems(items);

        const laborToggles = {};
        (orderData.linkedLaborItems || []).forEach((labor) => {
          laborToggles[labor.lineItemId] = {
            include: true,
            assemblyId: labor.assemblyId,
            lineItemId: labor.lineItemId,
            laborId: labor.laborId,
          };
        });
        setLaborVariances(laborToggles);

        if (poId) {
          const poRes = await axios.get(`${API_BASE}/purchaseOrders2/${poId}`, {
            headers,
          });
          setPO(poRes.data);
          const vendorId = orderData.vendor?._id || orderData.vendor;
          await fetchAvailablePOItems(
            poId,
            vendorId,
            items,
            headers,
            netQuantitiesMap,
          );
          await fetchAvailableBudgetItems(
            projectData._id,
            vendorId,
            poId,
            items,
            headers,
            netQuantitiesMap,
          );
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: `Failed to load data: ${err.message}`,
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, orderId]);

  const sitePdfItems = useMemo(() => {
    const groups = new Map();

    editableItems.forEach((item) => {
      if (Number(item.quantity) <= 0) return;
      const uom = String(item.uom ?? "-")
        .trim()
        .toUpperCase();
      const materialCode = String(item.materialCode ?? "NO-CODE").trim();
      const size = String(item.size ?? "-").trim();

      const key = [materialCode, size, uom].join("|||");

      if (!groups.has(key)) {
        groups.set(key, {
          materialCode,
          description: item.description || "Unnamed Material",
          size,
          uom,
          totalQuantity: 0,
          conditions: new Set(),
          bidAreas: new Set(),
        });
      }

      const group = groups.get(key);
      group.totalQuantity += Number(item.quantity);
      if (item.conditionName) group.conditions.add(item.conditionName);
      if (item.bidArea) group.bidAreas.add(item.bidArea);
    });

    return Array.from(groups.values())
      .map((group) => {
        let displayUom = group.uom;
        const upperUom = (group.uom || "").trim().toUpperCase();
        if (
          upperUom === "LF" ||
          upperUom === "SF" ||
          upperUom.includes("LINEAR") ||
          upperUom.includes("LINEAL") ||
          upperUom.includes("SQ FT") ||
          upperUom.includes("SQUARE")
        ) {
          displayUom = "PC";
        }

        return {
          description: `${group.materialCode} – ${group.description}`.trim(),
          size: group.size,
          uom: displayUom,
          quantity: group.totalQuantity.toLocaleString("en-US", {
            maximumFractionDigits: 0,
          }),
          // No price fields - site copy
          unitPrice: "",
          lineTotal: "",
        };
      })
      .sort((a, b) =>
        (a.materialCode || "").localeCompare(b.materialCode || ""),
      );
  }, [editableItems, itemsVersion]);

  const fetchAvailablePOItems = async (
    poId,
    vendorId,
    currentItems,
    headers,
    netQuantitiesMap,
  ) => {
    try {
      const poItemsRes = await axios.get(
        `${API_BASE}/assemblies/po/${poId}/items`,
        { headers },
      );
      const currentItemIds = new Set(
        currentItems.map((item) => item.lineItemId?.toString()),
      );

      // Build a map of this order's quantities so we can exclude them from pending
      const thisOrderQtyMap = new Map();
      currentItems.forEach((item) => {
        if (item.lineItemId) {
          thisOrderQtyMap.set(item.lineItemId.toString(), item.quantity || 0);
        }
      });

      const available = poItemsRes.data
        .filter((item) => {
          const itemVendorId = item.vendor?._id || item.vendor;
          const lineItemId = item._id?.toString();
          const isSameVendor = itemVendorId === vendorId;
          const notOnOrder = !currentItemIds.has(lineItemId);

          // Use net quantities to determine remaining
          const netData = netQuantitiesMap.get(lineItemId);

          let netRemaining;
          if (netData) {
            // The API pending includes ALL orders; since this item is NOT on
            // this order (we filtered above), no need to subtract thisOrderQty
            netRemaining =
              netData.netRemaining ??
              Math.max(
                0,
                (netData.netRequiredQty || 0) -
                  (netData.qtyOrdered || 0) -
                  (netData.qtyPending || 0),
              );
          } else {
            netRemaining = Math.max(
              0,
              (item.requiredQty || 0) -
                (item.qtyOrdered || 0) -
                (item.qtyPending || 0),
            );
          }

          return isSameVendor && notOnOrder;
        })
        .map((item) => {
          const netData = netQuantitiesMap.get(item._id?.toString());

          const effectivePrice =
            netData?.latestPrice ?? item.price ?? item.cost ?? 0;
          const effectiveSize = netData?.latestSize || item.size || "";
          const effectiveCoverage =
            netData?.latestCoveragePerUnit || item.coveragePerUnit || "";

          let netRemaining;
          if (netData) {
            netRemaining = Math.max(
              0,
              (netData.netRequiredQty || 0) -
                (netData.qtyOrdered || 0) -
                (netData.qtyPending || 0),
            );
          } else {
            netRemaining = Math.max(
              0,
              (item.requiredQty || 0) -
                (item.qtyOrdered || 0) -
                (item.qtyPending || 0),
            );
          }

          return {
            assemblyId: item.assemblyId,
            lineItemId: item._id,
            conditionName: item.conditionName || "",
            description: item.costCodeDescription || item.itemDescription || "",
            size: effectiveSize,
            uom: item.uom || item.container || "",
            price: effectivePrice,
            bidArea: item.bidArea || "",
            laborId: item.laborId || null,
            coveragePerUnit: effectiveCoverage,
            materialCode: item.materialCode || "",
            sectionNumber: item.sectionNumber || "",
            requiredQty:
              netData?.netRequiredQty ?? item.requiredQty ?? item.quantity ?? 0,
            remainingQty: netRemaining,
            isFullyOrdered: netRemaining <= 0, // ← ADD
            netRequiredQty: netData?.netRequiredQty ?? item.requiredQty ?? 0,
            qtyOrdered: netData?.qtyOrdered ?? 0,
            hasPriceChange: netData?.hasPriceChange || false,
            priceChangedFrom: netData?.priceChangedFrom || null,
          };
        });
      setAvailablePOItems(available);
    } catch (err) {
      console.error("Error fetching PO items:", err);
    }
  };

  const normalizeConditionName = (conditionName) => {
    if (!conditionName) return conditionName;
    const str = String(conditionName).trim();
    const match = str.match(/^\d+\s*[-–—]\s*(.+)$/);
    return match ? match[1].trim() : str;
  };

  const tableFilterOptions = useMemo(() => {
    const allConditions = [
      ...new Set(
        editableItems
          .map((i) => normalizeConditionName(i.conditionName))
          .filter(Boolean),
      ),
    ].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );

    const allSections = [
      ...new Set(editableItems.map((i) => i.sectionNumber).filter(Boolean)),
    ].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );

    const allBidAreas = [
      ...new Set(editableItems.map((i) => i.bidArea).filter(Boolean)),
    ].sort();

    const allUoms = [
      ...new Set(editableItems.map((i) => i.uom).filter(Boolean)),
    ].sort();

    // For size: show all sizes, but "in current filter" items first
    const allSizes = [
      ...new Set(editableItems.map((i) => i.size).filter(Boolean)),
    ].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );

    // Sizes that match active condition/section/bidArea/uom filters (to surface relevant sizes)
    const activeFilteredItems = editableItems.filter((item) => {
      const normCond = normalizeConditionName(item.conditionName);
      if (
        tableFilters.conditionName.length > 0 &&
        !tableFilters.conditionName.includes(normCond)
      )
        return false;
      if (
        tableFilters.sectionNumber.length > 0 &&
        !tableFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (
        tableFilters.bidArea.length > 0 &&
        !tableFilters.bidArea.includes(item.bidArea)
      )
        return false;
      if (tableFilters.uom.length > 0 && !tableFilters.uom.includes(item.uom))
        return false;
      return true;
    });

    const sizesInCurrentFilter = new Set(
      activeFilteredItems.map((i) => i.size).filter(Boolean),
    );

    // Build size options: "in filter" group first (if other filters active), then rest
    const hasOtherActiveFilters =
      tableFilters.conditionName.length > 0 ||
      tableFilters.sectionNumber.length > 0 ||
      tableFilters.bidArea.length > 0 ||
      tableFilters.uom.length > 0;

    const sizeOptions = hasOtherActiveFilters
      ? [
          ...allSizes
            .filter((s) => sizesInCurrentFilter.has(s))
            .map((s) => ({ value: s, label: s, inFilter: true })),
          ...allSizes
            .filter((s) => !sizesInCurrentFilter.has(s))
            .map((s) => ({ value: s, label: s, inFilter: false })),
        ]
      : allSizes.map((s) => ({ value: s, label: s, inFilter: false }));

    return {
      conditionName: allConditions,
      sectionNumber: allSections,
      bidArea: allBidAreas,
      uom: allUoms,
      size: allSizes, // plain strings for badge display
      sizeOptions, // enriched objects for the dropdown
    };
  }, [editableItems, tableFilters]);
  const filteredEditableItems = useMemo(() => {
    let items = editableItems.filter((item) => {
      if (item._removedFromOrder) return false;

      const normCond = normalizeConditionName(item.conditionName);

      if (
        tableFilters.conditionName.length > 0 &&
        !tableFilters.conditionName.includes(normCond)
      )
        return false;

      if (
        tableFilters.sectionNumber.length > 0 &&
        !tableFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;

      if (
        tableFilters.bidArea.length > 0 &&
        !tableFilters.bidArea.includes(item.bidArea)
      )
        return false;

      if (tableFilters.uom.length > 0 && !tableFilters.uom.includes(item.uom))
        return false;

      if (
        tableFilters.size.length > 0 &&
        !tableFilters.size.includes(item.size)
      )
        return false;

      return true;
    });

    if (tableFilters.descriptionSearch) {
      items = moMultiTokenSearch(items, tableFilters.descriptionSearch);
    }

    return items;
  }, [editableItems, tableFilters]);

  const fetchAvailableBudgetItems = async (
    projectId,
    vendorId,
    poId,
    currentItems,
    headers,
    netQuantitiesMap,
  ) => {
    try {
      const budgetItemsRes = await axios.get(
        `${API_BASE}/assemblies/project/${projectId}/materials`,
        {
          headers,
          params: { vendor: vendorId, showAssigned: true },
        },
      );
      const currentItemIds = new Set(
        currentItems.map((item) => item.lineItemId?.toString()),
      );
      const available = budgetItemsRes.data
        .filter((item) => {
          const lineItemId = item._id?.toString();
          const notOnOrder = !currentItemIds.has(lineItemId);
          const notOnThisPO =
            !item.linkedPO || item.linkedPO.toString() !== poId.toString();
          return notOnOrder && notOnThisPO;
        })
        .map((item) => {
          const netData = netQuantitiesMap.get(item._id?.toString());

          const effectivePrice =
            netData?.latestPrice ?? item.price ?? item.cost ?? 0;
          const effectiveSize = netData?.latestSize || item.size || "";

          let netRemaining;
          if (netData) {
            // These items are NOT on this order, so no need to subtract thisOrderQty
            netRemaining = Math.max(
              0,
              (netData.netRequiredQty || 0) -
                (netData.qtyOrdered || 0) -
                (netData.qtyPending || 0),
            );
          } else {
            netRemaining = Math.max(
              0,
              (item.requiredQty || 0) -
                (item.qtyOrdered || 0) -
                (item.qtyPending || 0),
            );
          }

          return {
            assemblyId: item.assemblyId,
            lineItemId: item._id,
            conditionName: item.conditionName || "",
            description: item.costCodeDescription || item.itemDescription || "",
            size: effectiveSize,
            uom: item.uom || item.container || "",
            price: effectivePrice,
            bidArea: item.bidArea || "",
            laborId: item.laborId || null,
            coveragePerUnit:
              netData?.latestCoveragePerUnit || item.coveragePerUnit || "",
            materialCode: item.materialCode || "",
            sectionNumber: item.sectionNumber || "",
            requiredQty:
              netData?.netRequiredQty ?? item.requiredQty ?? item.quantity ?? 0,
            netRemaining,
          };
        });
      setAvailableBudgetItems(available);
    } catch (err) {
      console.error("Error fetching budget items:", err);
    }
  };

  const handleConvertToStockOut = async () => {
    setIsLoadingStockOut(true);
    setStockOutVendors([]);
    setStockOutSelectedVendor(null);
    setStockOutMissing([]);
    setStockOutReasonCategory("");
    setStockOutReasonText("");

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Call find-vendors with all items on this order
      const lineItems = editableItems.map((item) => ({
        lineItemId: item.lineItemId,
        description: item.description || "",
        currentVendorId: order.vendor?._id || order.vendor,
      }));

      const res = await axios.post(
        `${API_BASE}/stock-out-orders/find-vendors`,
        { lineItems },
        { headers },
      );

      if (!res.data.success) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: "Failed to find alternate vendors.",
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      // Build a map of vendorId -> { vendorName, matchedCount, totalItems, missingItems }
      const vendorMap = new Map();
      const totalItems = res.data.results.length;

      res.data.results.forEach((result) => {
        if (!result.matched || result.vendors.length === 0) return;
        result.vendors.forEach((v) => {
          if (!vendorMap.has(v.vendorId)) {
            vendorMap.set(v.vendorId, {
              vendorId: v.vendorId,
              vendorName: v.vendorName,
              matchedCount: 0,
              totalItems,
              items: [],
            });
          }
          const entry = vendorMap.get(v.vendorId);
          entry.matchedCount += 1;
          entry.items.push({
            lineItemId: result.lineItemId,
            description: result.description,
            price: v.price,
          });
        });
      });

      // Only show vendors that cover ALL items
      const allVendors = Array.from(vendorMap.values()).sort(
        (a, b) => b.matchedCount - a.matchedCount,
      );

      // Filter to only vendors that match ALL items
      const fullCoverageVendors = allVendors.filter(
        (v) => v.matchedCount === totalItems,
      );

      setStockOutVendors(allVendors);
      setShowStockOutModal(true);

      if (fullCoverageVendors.length === 0 && allVendors.length > 0) {
        // Show which items are missing for the best vendor
        const best = allVendors[0];
        const matchedIds = new Set(best.items.map((i) => i.lineItemId));
        const missing = res.data.results
          .filter((r) => !matchedIds.has(r.lineItemId))
          .map((r) => r.description || r.lineItemId);
        setStockOutMissing(missing);
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message,
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsLoadingStockOut(false);
    }
  };

  // ============================================================
  // CORRECTED groupedItems MEMO - Replace the existing one
  // ============================================================

  const groupedItems = useMemo(() => {
    if (!groupByAssembly) return null;

    const map = new Map();
    filteredEditableItems.forEach((item) => {
      const conditionName = item.conditionName || "Uncategorized";
      const materialCode = item.materialCode || "NO-CODE";
      const uom = item.uom || "N/A";
      const size = item.size || "N/A";
      const price = safeNumber(item.price, 0);
      // Include price in the grouping key since same material/size should have same price
      const key = `${materialCode}|||${size}|||${uom}|||${price}`;

      if (!map.has(key)) {
        map.set(key, {
          conditionName,
          materialCode,
          description: item.description || "",
          size,
          uom,
          price, // Store the price for the group
          bidArea: item.bidArea || "-",
          _isGroup: true,
          _groupKey: key,
          _count: 0,
          _totalQuantity: 0,
          _totalBudgetedQty: 0,
          _totalNetRequired: 0,
          _totalOrdered: 0,
          _totalPending: 0,
          _totalRemaining: 0,
          _totalLineTotal: 0,
          _lineItemIds: [],
          _items: [],
        });
      }

      const group = map.get(key);
      group._count += 1;
      group._totalQuantity += safeNumber(item.quantity, 0);
      group._totalBudgetedQty += safeNumber(item.budgetedQty, 0);
      group._totalNetRequired += safeNumber(item.netRequiredQty, 0);
      group._totalOrdered += safeNumber(item.qtyOrdered, 0);
      group._totalPending += safeNumber(item.qtyPending, 0);
      group._totalRemaining += safeNumber(item.remainingAvailable, 0);
      group._totalLineTotal += calculateLineTotal(item);
      group._lineItemIds.push(item.lineItemId);
      group._items.push(item);

      if (item.bidArea && !group.bidArea.includes(item.bidArea)) {
        group.bidArea =
          group.bidArea === "-"
            ? item.bidArea
            : `${group.bidArea}, ${item.bidArea}`;
      }
    });

    return Array.from(map.values());
  }, [filteredEditableItems, groupByAssembly]);

  // Items to display in the table
  const displayTableItems = useMemo(() => {
    return groupByAssembly ? groupedItems : filteredEditableItems;
  }, [groupByAssembly, groupedItems, filteredEditableItems]);

  const filteredTotals = useMemo(() => {
    const itemsTotal = filteredEditableItems.reduce((sum, item) => {
      const lineTotal = calculateLineTotal(item);
      return sum + (isNaN(lineTotal) ? 0 : lineTotal);
    }, 0);

    return {
      itemCount: filteredEditableItems.length,
      itemsTotal: isNaN(itemsTotal) ? 0 : itemsTotal,
      totalQuantity: filteredEditableItems.reduce(
        (sum, i) => sum + safeNumber(i.quantity, 0),
        0,
      ),
      totalBudgeted: filteredEditableItems.reduce(
        (sum, i) => sum + safeNumber(i.netRequiredQty, 0),
        0,
      ),
    };
  }, [filteredEditableItems]);

  const hasActiveFilters = useMemo(() => {
    return Object.values(tableFilters).some((f) =>
      Array.isArray(f) ? f.length > 0 : !!f,
    );
  }, [tableFilters]);

  const handleTableFilterChange = useCallback((type, value) => {
    if (type === "descriptionSearch") {
      setTableFilters((prev) => ({ ...prev, descriptionSearch: value }));
    } else {
      setTableFilters((prev) => ({ ...prev, [type]: value || [] }));
    }
  }, []);

  const clearAllTableFilters = useCallback(() => {
    setTableFilters({
      conditionName: [],
      sectionNumber: [],
      bidArea: [],
      uom: [],
      size: [], // ADD THIS
      descriptionSearch: "",
    });
  }, []);

  const toggleTableFilter = useCallback((field) => {
    setShowTableFilter((prev) => (prev === field ? null : field));
  }, []);

  const handleExecuteStockOutConversion = async () => {
    if (!stockOutSelectedVendor) {
      setAlert({
        open: true,
        type: "error",
        title: "No Vendor Selected",
        message: "Please select a vendor to convert to.",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    // Check if vendor covers all items
    const vendor = stockOutVendors.find(
      (v) => v.vendorId === stockOutSelectedVendor,
    );
    const totalItems = editableItems.length;

    if (!vendor || vendor.matchedCount < totalItems) {
      setAlert({
        open: true,
        type: "error",
        title: "Incomplete Coverage",
        message: `This vendor only has pricing for ${vendor?.matchedCount || 0} of ${totalItems} items. All items must be available from the new vendor to proceed.`,
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.put(
        `${API_BASE}/stock-out-orders/convert-from-mo/${orderId}`,
        {
          newVendorId: stockOutSelectedVendor,
          stockOutReason:
            stockOutReasonText ||
            stockOutReasonCategory ||
            "Vendor unable to fulfil order",
          stockOutReasonCategory,
        },
        { headers },
      );

      setShowStockOutModal(false);

      setAlert({
        open: true,
        type: "success",
        title: "Converted to Stock-Out",
        message: res.data.message,
        confirmText: "View Stock-Out Order",
        cancelText: "Back to Materials",
        onConfirm: () => {
          setAlert((prev) => ({ ...prev, open: false }));
          // Navigate to the new stock-out order
          navigate(
            `/projects/${id}/stock-out-order/${res.data.stockOutOrder._id}`,
          );
        },
        onClose: () => {
          setAlert((prev) => ({ ...prev, open: false }));
          navigate(`/projects/${id}`, { state: { initialTab: "Materials" } });
        },
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Conversion Failed",
        message: err.response?.data?.message || err.message,
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
/**
/**
/**
 * VARIANCE SUMMARY CALCULATION
 *
 * Size Change Logic:
 * 1. Convert remaining budget SF to equivalent qty at new size
 * 2. Closeout old spec, create new spec with equivalent qty
 * 3. NO labor variance if delivered <= equivalent qty
 * 4. Labor variance ONLY on overage (delivered - equivalent) portion
 *
 * The material variance is just the price difference (if any)
 * Labor variance only when exceeding budgeted coverage
 */
  const varianceSummary = useMemo(() => {
    const changes = [];
    let totalMaterialDelta = 0;
    const laborCoverageChanges = [];

    editableItems.forEach((item) => {
      const currentCoverage = getCoverage(item);
      const deliveredQty = safeNumber(item.quantity, 0);
      const deliveredPrice = safeNumber(item.price, 0);

      const origPrice = safeNumber(item.originalPrice, 0);
      const origSize = item.originalSize || "";
      const originalCoverage = getCoverage({ ...item, size: origSize });

      const uomLower = (item.uom || "").toLowerCase();
      const isCoverageUom =
        uomLower.includes("sf") ||
        uomLower.includes("square") ||
        uomLower.includes("sq") ||
        uomLower === "lf" ||
        uomLower.includes("linear") ||
        uomLower.includes("lin ft") ||
        uomLower.includes("lin.ft") ||
        uomLower.includes("lin");

      const sizeChanged =
        isCoverageUom &&
        item.size.trim() !== origSize.trim() &&
        origSize.trim() !== "" &&
        item.size.trim() !== "";

      const priceChanged = Math.abs(deliveredPrice - origPrice) > 0.001;

      const remainingAvailable = safeNumber(item.remainingAvailable, 0);
      const qtyOrdered = safeNumber(item.qtyOrdered, 0);
      const netRequiredQty = safeNumber(item.netRequiredQty, 0);

      // ---- NEW/VARIANCE ITEMS ----
      if (item.isVarianceItem) {
        const amount = deliveredQty * currentCoverage * deliveredPrice;
        if (deliveredQty > 0) {
          changes.push({
            type: "new_item",
            description: item.description || "New Item",
            orderedQty: deliveredQty,
            spec: `${item.size || "-"} @ $${deliveredPrice.toFixed(2)}`,
            coveragePerUnit: currentCoverage.toFixed(2),
            uom: item.uom,
            delta: amount,
          });
          totalMaterialDelta += amount;

          if (item.laborId) {
            laborCoverageChanges.push({
              materialLineItemId: item.lineItemId,
              assemblyId: item.assembly,
              laborId: item.laborId,
              description: item.description || "New Item",
              coverageDelta: deliveredQty * currentCoverage,
            });
          }
        }
        return;
      }

      // ---- SIZE CHANGE ----
      if (sizeChanged && originalCoverage > 0 && currentCoverage > 0) {
        const totalBudgetSF = remainingAvailable * originalCoverage;
        const equivalentQtyAtNewSize = Math.ceil(
          totalBudgetSF / currentCoverage,
        );

        // Always the full equivalent conversion cost difference
        const oldSpecCost = remainingAvailable * originalCoverage * origPrice;
        const newSpecCost =
          equivalentQtyAtNewSize * currentCoverage * deliveredPrice;
        const conversionDelta = newSpecCost - oldSpecCost;

        // Overage = ordering more than equivalent budget
        const exceedsEquivalent = deliveredQty > equivalentQtyAtNewSize;
        const overageQty = exceedsEquivalent
          ? deliveredQty - equivalentQtyAtNewSize
          : 0;
        const overageCost = overageQty * currentCoverage * deliveredPrice;

        // Total delta: conversion difference + any overage cost
        const delta = conversionDelta + overageCost;

        totalMaterialDelta += delta;

        changes.push({
          type: "spec_change",
          description: item.description || "Material Item",
          uom: item.uom,
          originalSpec: `${origSize} @ $${origPrice.toFixed(2)}`,
          originalSFPerUnit: originalCoverage.toFixed(2),
          currentSpec: `${item.size} @ $${deliveredPrice.toFixed(2)}`,
          currentSFPerUnit: currentCoverage.toFixed(2),
          budgetQty: remainingAvailable,
          budgetSF: totalBudgetSF.toFixed(1),
          oldSpecCost,
          equivalentQtyAtNewSize,
          equivalentSF: (equivalentQtyAtNewSize * currentCoverage).toFixed(1),
          newSpecCost,
          conversionDelta,
          deliveredQty,
          remainingAfterDelivery: Math.max(
            0,
            equivalentQtyAtNewSize - deliveredQty,
          ),
          exceedsEquivalent,
          overageQty,
          overageCost,
          delta,
          qtyOrdered,
          netRequiredQty,
          laborId: item.laborId,
        });

        if (overageQty > 0 && item.laborId) {
          laborCoverageChanges.push({
            materialLineItemId: item.lineItemId,
            assemblyId: item.assembly,
            laborId: item.laborId,
            description: item.description || "Material Item",
            coverageDelta: overageQty * currentCoverage,
            isOverage: true,
          });
        }

        return;
      }
      // ---- NO SIZE CHANGE ----
      let materialDelta = 0;

      const exceedsBudget = deliveredQty > remainingAvailable;
      const overageQty = exceedsBudget ? deliveredQty - remainingAvailable : 0;

      if (overageQty > 0) {
        const overageAmount = overageQty * currentCoverage * deliveredPrice;
        materialDelta += overageAmount;

        changes.push({
          type: "qty_overage",
          description: item.description || "Material Item",
          uom: item.uom,
          netRequiredQty,
          qtyOrdered,
          remainingAvailable,
          orderedQty: deliveredQty,
          overageQty,
          spec: `${item.size || "-"} @ $${deliveredPrice.toFixed(2)}`,
          coveragePerUnit: currentCoverage.toFixed(2),
          delta: overageAmount,
          laborId: item.laborId,
          totalCommitted: safeNumber(item.totalCommitted, 0),
          projectedOverage: safeNumber(item.projectedOverage, 0),
          isOverCommitted: item.isOverCommitted || false,
          otherPending: safeNumber(item.otherPending, 0),
        });

        if (item.laborId) {
          laborCoverageChanges.push({
            materialLineItemId: item.lineItemId,
            assemblyId: item.assembly,
            laborId: item.laborId,
            description: item.description || "Material Item",
            coverageDelta: overageQty * currentCoverage,
            isOverage: true,
          });
        }
      }

      if (priceChanged) {
        const oldCost = remainingAvailable * currentCoverage * origPrice;
        const newCost = remainingAvailable * currentCoverage * deliveredPrice;
        const priceDelta = newCost - oldCost;

        if (Math.abs(priceDelta) > 0.01) {
          materialDelta += priceDelta;

          changes.push({
            type: "price_change",
            description: item.description || "Material Item",
            uom: item.uom,
            netRequiredQty,
            qtyOrdered,
            remainingBudgetQty: remainingAvailable,
            deliveredQty,
            originalPrice: origPrice.toFixed(2),
            currentPrice: deliveredPrice.toFixed(2),
            priceDiff: (deliveredPrice - origPrice).toFixed(2),
            coveragePerUnit: currentCoverage.toFixed(2),
            oldCost,
            newCost,
            delta: priceDelta,
          });
        }
      }

      if (Math.abs(materialDelta) > 0.01) {
        totalMaterialDelta += materialDelta;
      }
    });

    // Process labor changes
    const finalLaborChanges = [];
    laborCoverageChanges.forEach((change) => {
      const matchingLabors = linkedLaborItems.filter(
        (l) =>
          l.assemblyId?.toString() === change.assemblyId?.toString() &&
          l.laborId === change.laborId,
      );

      matchingLabors.forEach((labor) => {
        const baseRate = safeNumber(labor.unitRate, 0);
        const rate = laborRateOverrides[labor.lineItemId] ?? baseRate;
        const key = `${change.materialLineItemId}_${labor.lineItemId}`;
        const delta = laborDeltaOverrides[key] ?? change.coverageDelta;
        const cost = delta * rate;

        if (Math.abs(cost) > 0.01) {
          finalLaborChanges.push({
            materialLineItemId: change.materialLineItemId,
            laborLineItemId: labor.lineItemId,
            uom: labor.uom,
            laborId: labor.laborId,
            assemblyId: labor.assemblyId,
            description:
              labor.costCodeDescription || labor.itemDescription || "Labor",
            coverageDelta: delta,
            unitRate: rate,
            deltaCost: cost,
            included:
              laborVariances[change.materialLineItemId]?.include ?? true,
            isOverage: change.isOverage || false,
          });
        }
      });
    });

    const totalLaborDelta = finalLaborChanges
      .filter((lc) => lc.included)
      .reduce((sum, lc) => sum + lc.deltaCost, 0);

    return {
      changes,
      laborChanges: finalLaborChanges,
      totalMaterialDelta,
      totalLaborDelta,
      totalDelta: totalMaterialDelta + totalLaborDelta,
      hasChanges: changes.length > 0 || finalLaborChanges.length > 0,
    };
  }, [
    editableItems,
    linkedLaborItems,
    laborVariances,
    laborRateOverrides,
    laborDeltaOverrides,
    itemsVersion,
  ]);

  // ─── Local multi-token search (adapted from searchUtils.js) ───────────────────

  function normaliseSearch(str) {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .replace(/[''"""]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function buildMOSearchBlob(item) {
    const parts = [
      item.materialCode || "",
      item.description || "",
      item.conditionName || "",
      item.sectionNumber || "",
      item.size || "",
      item.uom || "",
      item.bidArea || "",
      item.laborId || "",
    ];
    return normaliseSearch(parts.join("  "));
  }

  function moTokenVariants(token) {
    const variants = [token];

    // Match exactly 3 digits: e.g. "358" → a="3", b="5", c="8"
    const fractionCondensed = token.match(/^(\d)(\d)(\d)$/);
    if (fractionCondensed) {
      const [, a, b, c] = fractionCondensed;
      variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`, `${a}${b}/${c}`);
    }

    // Match 4 digits: e.g. "3512" → "3-5/12" (whole + numerator + 2-digit denominator)
    const fractionCondensed4 = token.match(/^(\d)(\d)(\d{2})$/);
    if (fractionCondensed4) {
      const [, a, b, c] = fractionCondensed4;
      variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`);
    }

    const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d+)$/);
    if (fractionExpanded) {
      const [, a, b, c] = fractionExpanded;
      variants.push(`${a}${b}${c}`);
    }

    const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
    if (gaugeMatch) {
      const [, num] = gaugeMatch;
      variants.push(`${num}ga`, `${num} ga`, `${num}gauge`);
    }

    return variants;
  }

  function scoreMOItem(item, tokens) {
    let score = 0;
    const fullQuery = tokens.join(" ");
    const code = normaliseSearch(item.materialCode);
    const desc = normaliseSearch(item.description);
    const cond = normaliseSearch(item.conditionName);
    const size = normaliseSearch(item.size);

    if (code === fullQuery) score += 10000;
    else if (code.startsWith(fullQuery)) score += 5000;
    else if (code.includes(fullQuery)) score += 2000;

    if (desc === fullQuery) score += 8000;
    else if (desc.startsWith(fullQuery)) score += 3000;

    const fields = [
      { text: code, weight: 50 },
      { text: desc, weight: 30 },
      { text: cond, weight: 15 },
      { text: size, weight: 12 },
      { text: normaliseSearch(item.sectionNumber), weight: 10 },
      { text: normaliseSearch(item.uom), weight: 5 },
    ];

    for (const token of tokens) {
      const variants = moTokenVariants(token);
      for (const { text, weight } of fields) {
        if (!text) continue;
        for (const v of variants) {
          if (text === v) {
            score += weight * 3;
            break;
          } else if (text.startsWith(v)) {
            score += weight * 2;
            break;
          } else if (text.includes(v)) {
            score += weight;
            break;
          }
        }
      }
    }

    score -= desc.length * 0.01;
    return score;
  }

  function moMultiTokenSearch(items, searchStr) {
    const raw = normaliseSearch(searchStr);
    if (!raw) return items;
    const tokens = raw.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return items;

    const matched = items.filter((item) => {
      const blob = buildMOSearchBlob(item);
      return tokens.every((token) =>
        moTokenVariants(token).some((v) => blob.includes(v)),
      );
    });

    return matched
      .map((item) => ({ item, score: scoreMOItem(item, tokens) }))
      .sort((a, b) => b.score - a.score)
      .map((e) => e.item);
  }
  // ─────────────────────────────────────────────────────────────────────────────

  // REPLACE the existing filteredPOItems useMemo
  const filteredPOItems = useMemo(() => {
    return moMultiTokenSearch(availablePOItems, addItemSearch);
  }, [availablePOItems, addItemSearch]);

  // REPLACE the existing filteredBudgetItems useMemo
  const filteredBudgetItems = useMemo(() => {
    return moMultiTokenSearch(availableBudgetItems, newItemSearch);
  }, [availableBudgetItems, newItemSearch]);

  const formatCurrency = useCallback((num) => {
    return Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const handleSizeChangeWithAutoQty = useCallback((lineItemId, newSize) => {
    setEditableItems((prev) =>
      prev.map((item) => {
        if (item.lineItemId !== lineItemId) return item;
        return { ...item, size: newSize, autoAdjustedQty: null };
      }),
    );
    setItemsVersion((v) => v + 1);
  }, []);

  const handleItemChange = useCallback((lineItemId, field, value) => {
    setEditableItems((prev) =>
      prev.map((item) => {
        if (item.lineItemId !== lineItemId) return item;

        const updated = { ...item, [field]: value };

        // If user manually changes quantity, clear the auto-adjusted flag
        if (field === "quantity") {
          updated.autoAdjustedQty = null;
        }

        return updated;
      }),
    );
    setItemsVersion((v) => v + 1);
  }, []);

  const toggleEditItem = useCallback((lineItemId) => {
    setEditingItemId((prev) => (prev === lineItemId ? null : lineItemId));
  }, []);

  const handleAddFromPO = useCallback(
    (poItem) => {
      // Use the price from poItem which now has latestPrice from net data
      const itemPrice = safeNumber(poItem.price, 0);
      const netData = getNetItemData(poItem.lineItemId);
      const baseQty = safeNumber(poItem.requiredQty, 0);

      // Use remainingQty which is already calculated from netRemaining
      const remainingFromPO = safeNumber(poItem.remainingQty, 0);
      const maxQty = remainingFromPO > 0 ? remainingFromPO : Infinity;
      const newItem = {
        assembly: poItem.assemblyId,
        lineItemId: poItem.lineItemId,
        quantity: 0,
        size: poItem.size || "",
        price: itemPrice,
        bidArea: poItem.bidArea || "",
        budgetedQty: baseQty,
        isVarianceItem: maxQty === Infinity,
        showMaxAsUnlimited: maxQty === Infinity,
        netRequiredQty: netData?.netRequiredQty ?? baseQty,
        qtyOrdered: netData?.qtyOrdered ?? 0,
        qtyPending: netData?.qtyPending ?? 0,
        remainingAvailable: maxQty === Infinity ? 0 : maxQty,
        maxQty,
        conditionName: poItem.conditionName || "",
        description: poItem.description || "",
        uom: poItem.uom || "",
        laborId: isValidLaborId(poItem.laborId) ? poItem.laborId : null,
        coveragePerUnit: poItem.coveragePerUnit || "",
        materialCode: poItem.materialCode || "",
        sectionNumber: poItem.sectionNumber || "",
        isAddedFromPO: true,
        totalCommitted: (netData?.qtyOrdered ?? 0) + (netData?.qtyPending ?? 0),
        projectedOverage: Math.max(
          0,
          (netData?.qtyOrdered ?? 0) +
            (netData?.qtyPending ?? 0) -
            (netData?.netRequiredQty ?? baseQty),
        ),
        isOverCommitted:
          (netData?.qtyOrdered ?? 0) + (netData?.qtyPending ?? 0) >
          (netData?.netRequiredQty ?? baseQty),
        originalQuantity: 0,
        // Use the current (possibly updated) size/price as "original"
        originalSize: poItem.size || "",
        originalPrice: itemPrice,
        // Track if there was a prior price change
        hasPriorPriceChange: poItem.hasPriceChange || false,
        priorPriceChangedFrom: poItem.priceChangedFrom || null,
      };
      setEditableItems((prev) => [...prev, newItem]);
      setAvailablePOItems((prev) =>
        prev.filter((item) => item.lineItemId !== poItem.lineItemId),
      );
      setEditingItemId(poItem.lineItemId);
      setShowAddFromPO(false);
    },
    [getNetItemData],
  );

  const handleRemoveItem = useCallback((lineItemId) => {
    setEditableItems((prev) => {
      const item = prev.find((i) => i.lineItemId === lineItemId);
      if (!item) return prev;

      if (item.isAddedFromPO) {
        setAvailablePOItems((avail) => [
          ...avail,
          {
            assemblyId: item.assembly,
            lineItemId: item.lineItemId,
            conditionName: item.conditionName,
            description: item.description,
            size: item.size,
            uom: item.uom,
            price: item.price,
            bidArea: item.bidArea,
            laborId: item.laborId,
            coveragePerUnit: item.coveragePerUnit,
            materialCode: item.materialCode,
            sectionNumber: item.sectionNumber,
            remainingQty: item.maxQty,
            requiredQty: item.budgetedQty,
          },
        ]);
        return prev.filter((i) => i.lineItemId !== lineItemId);
      }

      if (item.isVarianceItem) {
        setAvailableBudgetItems((avail) => [
          ...avail,
          {
            assemblyId: item.assembly,
            lineItemId: item.lineItemId,
            conditionName: item.conditionName,
            description: item.description,
            size: item.size,
            uom: item.uom,
            price: item.price,
            bidArea: item.bidArea,
            laborId: item.laborId,
            coveragePerUnit: item.coveragePerUnit,
            materialCode: item.materialCode,
            sectionNumber: item.sectionNumber,
            requiredQty: item.budgetedQty,
          },
        ]);
        return prev.filter((i) => i.lineItemId !== lineItemId);
      }

      // Original PO item — zero out quantity so the PUT delta
      // releases the full pending on Save Changes
      return prev.map((i) =>
        i.lineItemId === lineItemId
          ? { ...i, quantity: 0, _removedFromOrder: true }
          : i,
      );
    });
    setEditingItemId(null);
    setItemsVersion((v) => v + 1);
  }, []);

  const handleLaborToggle = useCallback((lineItemId, include) => {
    setLaborVariances((prev) => ({
      ...prev,
      [lineItemId]: { ...prev[lineItemId], include },
    }));
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddSurcharge = useCallback(() => {
    setSurcharges((prev) => [...prev, { description: "", amount: 0 }]);
  }, []);

  const handleSurchargeChange = useCallback((index, field, value) => {
    setSurcharges((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  }, []);

  const handleRemoveSurcharge = useCallback((index) => {
    setSurcharges((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      let updateData = {};

      if (isEditable) {
        updateData = {
          shippingMethod: formData.shippingMethod,
          deliveryInstructions: formData.deliveryInstructions,
          expectedDeliveryDate: formData.expectedDeliveryDate || null,
          laborersRequired: formData.laborersRequired,
          laborersQty: formData.laborersQty,
          contact: formData.contact,
          fieldContact: formData.fieldContact,
          deliveryContact: formData.deliveryContact,
          revision: formData.revision,
          varianceReason: formData.varianceReason,
          varianceReasonCategory: formData.varianceReasonCategory,
          items: editableItems
            .filter((item) => !item._removedFromOrder)
            .map((item) => ({
              assembly: item.assembly,
              lineItemId: item.lineItemId,
              quantity: item.quantity,
              size: item.size,
              price: item.price,
              coveragePerUnit: getCoverage(item),
              bidArea: item.bidArea,
            })),
          surcharges,
        };
      } else {
        updateData = { surcharges };
      }

      await axios.put(`${API_BASE}/materialOrders2/${orderId}`, updateData, {
        headers,
      });

      setAlert({
        open: true,
        type: "success",
        title: "Saved",
        message: isEditable
          ? "Changes saved successfully!"
          : "Surcharges updated successfully!",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      console.error("Save error:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message,
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeliverOrder = async () => {
    if (varianceSummary.hasChanges && !formData.varianceReasonCategory.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Variance Reason Required",
        message:
          "Please select a reason category for the variances before marking as delivered.",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    const invalidItems = editableItems.filter((item) => item.quantity <= 0);
    if (invalidItems.length > 0) {
      setAlert({
        open: true,
        type: "error",
        title: "Invalid Quantities",
        message: "All items must have a quantity greater than 0.",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const laborVariancesArray = varianceSummary.laborChanges
        .filter((lc) => lc.included && Math.abs(lc.coverageDelta) > 0.001)
        .map((lc) => {
          const overriddenRate = laborRateOverrides[lc.laborLineItemId];
          const finalRate =
            overriddenRate !== undefined ? overriddenRate : lc.unitRate;

          const overriddenDeltaKey = `${lc.materialLineItemId}_${lc.laborLineItemId}`;
          const overriddenDelta = laborDeltaOverrides[overriddenDeltaKey];
          const finalDelta =
            overriddenDelta !== undefined ? overriddenDelta : lc.coverageDelta;

          return {
            laborId: lc.laborId,
            assemblyId: lc.assemblyId,
            lineItemId: lc.laborLineItemId,
            include: true,
            deltaQty: finalDelta,
            unitRate: finalRate,
          };
        });
      const deliverPayload = {
        deliveredAt: new Date().toISOString(),
        varianceReason: formData.varianceReason,
        varianceReasonCategory: formData.varianceReasonCategory,
        laborVariances: laborVariancesArray,
        items: editableItems
          .filter((item) => !item._removedFromOrder)
          .map((item) => ({
            assembly: item.assembly,
            lineItemId: item.lineItemId,
            quantity: item.quantity,
            size: item.size,
            price: item.price,
            bidArea: item.bidArea,
          })),
      };
      const deliverRes = await axios.put(
        `${API_BASE}/materialOrders2/${orderId}/deliver`,
        deliverPayload,
        { headers },
      );
      const hasVariance = deliverRes.data.varianceBudget != null;
      setOrder((prev) => ({ ...prev, status: "delivered" }));
      setAlert({
        open: true,
        type: "success",
        title: "Delivered",
        message: hasVariance
          ? `Order delivered! A variance of $${formatCurrency(
              deliverRes.data.varianceBudget?.grandTotal || 0,
            )} was created.`
          : "Order marked as delivered!",
        confirmText: "OK",
        onConfirm: () =>
          navigate(`/projects/${id}`, { state: { initialTab: "Materials" } }),
      });
    } catch (err) {
      console.error("Deliver error:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message,
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleVoidOrder = async () => {
    setAlert({
      open: true,
      type: "warning",
      title: "Confirm Void",
      message:
        "Are you sure you want to void this order? This will release all pending quantities.",
      confirmText: "Void Order",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          await axios.put(
            `${API_BASE}/materialOrders2/${orderId}/void`,
            { voidReason: "Voided by user" },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setOrder((prev) => ({ ...prev, status: "void" }));
          setAlert({
            open: true,
            type: "success",
            title: "Voided",
            message: "Order voided successfully!",
            confirmText: "OK",
            onConfirm: () =>
              navigate(`/projects/${id}`, {
                state: { initialTab: "Materials" },
              }),
          });
        } catch (err) {
          setAlert({
            open: true,
            type: "error",
            title: "Error",
            message: err.response?.data?.message || err.message,
            confirmText: "OK",
            onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
          });
        }
      },
      onClose: () => setAlert((prev) => ({ ...prev, open: false })),
    });
  };

  const supplierPdfItems = useMemo(() => {
    const groups = new Map();

    editableItems.forEach((item) => {
      if (Number(item.quantity) <= 0) return;
      const uom = String(item.uom ?? "-")
        .trim()
        .toUpperCase();
      const materialCode = String(item.materialCode ?? "NO-CODE").trim();
      const size = String(item.size ?? "-").trim();

      const key = [materialCode, size, uom].join("|||");

      if (!groups.has(key)) {
        groups.set(key, {
          materialCode,
          description: item.description || "Unnamed Material",
          size,
          uom,
          unitPrice: Number(item.price || 0),
          totalQuantity: 0,
          totalCoverage: 0,
          totalAmount: 0,
          conditions: new Set(),
          bidAreas: new Set(),
        });
      }

      const group = groups.get(key);
      const itemQty = Number(item.quantity);
      const coverage = getCoverage(item);

      group.totalQuantity += itemQty;
      group.totalCoverage += itemQty * coverage;
      group.totalAmount += itemQty * coverage * group.unitPrice;

      if (item.conditionName) group.conditions.add(item.conditionName);
      if (item.bidArea) group.bidAreas.add(item.bidArea);
    });

    return Array.from(groups.values())
      .map((group) => {
        let displayUom = group.uom;

        const upperUom = (group.uom || "").trim().toUpperCase();
        if (
          upperUom === "LF" ||
          upperUom === "SF" ||
          upperUom.includes("LINEAR") ||
          upperUom.includes("LINEAL") ||
          upperUom.includes("SQ FT") ||
          upperUom.includes("SQUARE")
        ) {
          displayUom = "PC";
        }

        return {
          description: `${group.materialCode} – ${group.description}`.trim(),
          size: group.size,
          uom: displayUom,
          quantity: group.totalQuantity.toLocaleString("en-US", {
            maximumFractionDigits: 0,
          }),
          up: `$${group.unitPrice.toFixed(2)}`,
          lineTotal: `$${group.totalAmount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        };
      })
      .sort((a, b) =>
        (a.materialCode || "").localeCompare(b.materialCode || ""),
      );
  }, [editableItems, itemsVersion]);

  const totals = useMemo(() => {
    const itemsTotal = editableItems.reduce((sum, item) => {
      const lineTotal = calculateLineTotal(item);
      return sum + (isNaN(lineTotal) ? 0 : lineTotal);
    }, 0);
    const surchargesTotal = surcharges.reduce(
      (sum, s) => sum + safeNumber(s.price, 0),
      0,
    );
    return {
      itemsTotal: isNaN(itemsTotal) ? 0 : itemsTotal,
      surchargesTotal: isNaN(surchargesTotal) ? 0 : surchargesTotal,
      total: itemsTotal + surchargesTotal,
    };
  }, [editableItems, surcharges, itemsVersion]);

  const contactOptions = useMemo(() => {
    const vendorId = order?.vendor?._id || order?.vendor;
    return contacts
      .filter((c) => c.relatedAccount === vendorId)
      .map((c) => ({
        value: c._id,
        label: `${c.firstName} ${c.lastName} (${c.email})`,
      }));
  }, [contacts, order]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u._id,
        label: `${u.displayName || "User"} (${u.phoneNumber || "N/A"})`,
      })),
    [users],
  );

  const getSafeCoverage = (coverage) => {
    if (
      coverage === null ||
      coverage === undefined ||
      coverage === "" ||
      coverage === "-" ||
      String(coverage).trim().toUpperCase() === "N/A" ||
      String(coverage).trim().toUpperCase() === "NONE"
    ) {
      return 1;
    }
    const num = Number(coverage);
    return isNaN(num) || num <= 0 ? 1 : num;
  };

  const isSFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return (
      u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
    );
  };

  const isLFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return u === "lf" || u.includes("linear") || u.includes("lin ft");
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-500",
      confirmed: "bg-blue-500",
      delivered: "bg-green-500",
      void: "bg-red-500",
    };
    return (
      <span
        className={`px-2.5 py-1 rounded text-white text-xs font-semibold uppercase tracking-wide ${
          colors[status] || "bg-gray-500"
        }`}
      >
        {status}
      </span>
    );
  };

  const generatePDF = async (sendEmail = false) => {
    if (isSaving || isLoading || !order || !project) return;
    setIsSaving(true);

    try {
      // ── Load templates ──────────────────────────────────
      const [vendorTemplateRes, siteTemplateRes] = await Promise.all([
        fetch("/material_order_template2.docx"),
        fetch("/material_order_template2_site.docx"),
      ]);

      if (!vendorTemplateRes.ok)
        throw new Error("Failed to load vendor template");
      if (!siteTemplateRes.ok) throw new Error("Failed to load site template");

      const vendorTemplateBuffer = await vendorTemplateRes.arrayBuffer();
      const siteTemplateBuffer = await siteTemplateRes.arrayBuffer();

      // ── Image handling (shared) ─────────────────────────
      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      const base64Parser = (tagValue) => {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64)) throw new Error("Invalid base64");
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes.buffer;
      };

      let imageBase64 = null;
      if (companyProfile?.logo) {
        if (companyProfile.logo.startsWith("data:image")) {
          imageBase64 = companyProfile.logo;
        } else {
          try {
            const logoRes = await fetch(companyProfile.logo);
            if (logoRes.ok) {
              const logoBlob = await logoRes.blob();
              imageBase64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(logoBlob);
              });
            }
          } catch (err) {
            console.warn("Failed to load logo:", err);
          }
        }
      }

      const imageOptions = {
        getImage: base64Parser,
        getSize: () => [125, 90],
      };

      // ── Shared data fields ──────────────────────────────
      const vendor =
        vendors.find((v) => v._id === (order.vendor?._id || order.vendor)) ||
        {};

      const vendorContacts =
        contacts
          .filter((c) => order.contact?.includes(c._id))
          .map((c) => `${c.firstName} ${c.lastName} (${c.email})`)
          .join(", ") || "N/A";

      const fieldContactsText =
        order.fieldContact
          ?.map(
            (fc) => `${fc.displayName || "User"} (${fc.phoneNumber || "N/A"})`,
          )
          .join(", ") || "N/A";

      const deliveryContactUser = order.deliveryContact
        ? users.find((u) => u._id === order.deliveryContact)
        : null;
      const deliveryContactText = deliveryContactUser
        ? `${deliveryContactUser.displayName || "User"} (${
            deliveryContactUser.phoneNumber || "N/A"
          })`
        : "N/A";

      const createdAt = order.createdAt
        ? new Date(order.createdAt).toLocaleDateString("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "N/A";

      const expectedDeliveryDate = order.expectedDeliveryDate
        ? new Date(order.expectedDeliveryDate).toLocaleDateString("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "N/A";

      const projectLocation = project?.address
        ? [
            project.address.street,
            project.address.city,
            `${project.address.state} ${project.address.postalCode}`,
            project.address.country,
          ]
            .filter((part) => part && part.trim() !== "")
            .join(", ") || "N/A"
        : project?.location || "N/A";

      const revisionLabel = ["delivered", "void"].includes(order.status)
        ? "FINAL"
        : order.revision > 0
          ? `R${order.revision}`
          : "";

      let taxRate = 0;
      if (
        companyProfile?.plugins?.quickbooks?.connected &&
        companyProfile?.defaultTax?.taxRate
      ) {
        taxRate = parseFloat(companyProfile.defaultTax.taxRate) || 0;
      } else if (companyProfile?.defaultTax?.taxRate) {
        taxRate = parseFloat(companyProfile.defaultTax.taxRate) || 0;
      }

      const sharedData = {
        companyName: companyProfile?.companyName || "Your Company",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country || ""}`
          : "N/A",
        companyPhone: companyProfile?.phone || "N/A",
        projectName: project?.projectName || "Project",
        projectLocation,
        vendorName: vendor.companyName || "Vendor",
        vendorAddress: vendor.address
          ? `${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.postalCode}, ${vendor.address.country || ""}`
          : "N/A",
        vendorContacts,
        fieldContacts: fieldContactsText,
        deliveryContact:
          order.shippingMethod === "Pickup" ? deliveryContactText : "N/A",
        materialOrderNumber: order.materialOrderNumber || "MO#",
        poNumber: `${project.projectNumber}`,
        createdAt,
        expectedDeliveryDate,
        shippingMethod: order.shippingMethod || "Delivery",
        deliveryInstructions: order.deliveryInstructions || "N/A",
        revision: revisionLabel,
        image: imageBase64,
      };

      // ── Helper: generate a single PDF from template + data ──
      const generateSinglePDF = async (
        templateBuffer,
        templateData,
        fileLabel,
      ) => {
        const zip = new PizZip(templateBuffer);
        const doc = new Docxtemplater(zip, {
          paragraphLoop: true,
          linebreaks: true,
          modules: [new ImageModule(imageOptions)],
        });
        doc.setData(templateData);
        doc.render();

        const docxBlob = doc.getZip().generate({ type: "blob" });

        const formDataPdf = new FormData();
        formDataPdf.append(
          "docx",
          docxBlob,
          `MO_${order.materialOrderNumber}_${fileLabel}.docx`,
        );
        formDataPdf.append("documentType", "MaterialOrder");
        formDataPdf.append(
          "identifier",
          `${order.materialOrderNumber}_${fileLabel}`,
        );

        const token = localStorage.getItem("token");
        const pdfRes = await axios.post(
          `${API_BASE}/upload/convert-to-pdf`,
          formDataPdf,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          },
        );

        if (!pdfRes.headers["content-type"]?.includes("application/pdf")) {
          const text = await pdfRes.data.text();
          throw new Error(
            `PDF conversion failed for ${fileLabel}: ${text || "Unknown error"}`,
          );
        }

        return new Blob([pdfRes.data], { type: "application/pdf" });
      };

      // ── VENDOR PDF (with pricing) ───────────────────────
      const vendorItems = [...supplierPdfItems];
      surcharges.forEach((s) => {
        vendorItems.push({
          description: s.description || "Surcharge",
          size: "-",
          uom: "-",
          quantity: "1",
          up: `$${safeNumber(s.price).toFixed(2)}`,
          lineTotal: `$${formatCurrency(safeNumber(s.price))}`,
        });
      });

      const subtotal = totals.itemsTotal + totals.surchargesTotal;
      const tax = subtotal * (taxRate / 100);
      const grandTotal = subtotal + tax;

      const vendorData = {
        ...sharedData,
        items: vendorItems,
        subtotal: `$${formatCurrency(subtotal)}`,
        taxRate: taxRate > 0 ? `${taxRate}%` : "0%",
        taxAmount: `$${formatCurrency(tax)}`,
        grandTotal: `$${formatCurrency(grandTotal)}`,
      };

      const vendorPdf = await generateSinglePDF(
        vendorTemplateBuffer,
        vendorData,
        "VENDOR",
      );

      // ── SITE PDF (no pricing) ───────────────────────────
      const siteData = {
        ...sharedData,
        items: sitePdfItems, // uses the no-price items
        // These fields exist in the template but will be empty/hidden
        // The _site template should not have price columns, but we
        // provide empty values as fallback in case template references them
        subtotal: "",
        taxRate: "",
        taxAmount: "",
        grandTotal: "",
      };

      const sitePdf = await generateSinglePDF(
        siteTemplateBuffer,
        siteData,
        "SITE",
      );

      // ── Route based on action ───────────────────────────
      if (sendEmail) {
        setVendorPdfBuffer(vendorPdf);
        setSitePdfBuffer(sitePdf);
        setShowEmailForm(true);
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message:
            "PDFs generated successfully! Please fill out the email form.",
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
      } else {
        // Download both PDFs
        const fileRevision = ["delivered", "void"].includes(order.status)
          ? "FINAL"
          : order.revision > 0
            ? `R${order.revision}`
            : "DRAFT";

        saveAs(
          vendorPdf,
          `MaterialOrder_${order.materialOrderNumber}_${fileRevision}.pdf`,
        );

        // Small delay so browser handles both downloads
        setTimeout(() => {
          saveAs(
            sitePdf,
            `MaterialOrder_${order.materialOrderNumber}_${fileRevision}_SITE.pdf`,
          );
        }, 500);

        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Vendor PDF and Site PDF downloaded!",
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
      }
    } catch (err) {
      console.error("PDF generation error:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.message || "Failed to generate PDFs",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Keep handleDownloadPDF as a wrapper
  const handleDownloadPDF = () => generatePDF(false);

  // NEW
  // Replace the existing handleSendEmail function with this:

  const handleSendEmail = async () => {
    if (!userEmail) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "User email not available. Please log in again.",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (isEditable) {
      setIsSaving(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const updateData = {
          shippingMethod: formData.shippingMethod,
          deliveryInstructions: formData.deliveryInstructions,
          expectedDeliveryDate: formData.expectedDeliveryDate || null,
          laborersRequired: formData.laborersRequired,
          laborersQty: formData.laborersQty,
          contact: formData.contact,
          fieldContact: formData.fieldContact,
          deliveryContact: formData.deliveryContact,
          revision: formData.revision,
          varianceReason: formData.varianceReason,
          varianceReasonCategory: formData.varianceReasonCategory,
          items: editableItems
            .filter((item) => !item._removedFromOrder)
            .map((item) => ({
              assembly: item.assembly,
              lineItemId: item.lineItemId,
              quantity: item.quantity,
              size: item.size,
              price: item.price,
              coveragePerUnit: getCoverage(item),
              bidArea: item.bidArea,
            })),
          surcharges,
        };

        // Save changes
        await axios.put(`${API_BASE}/materialOrders2/${orderId}`, updateData, {
          headers,
        });

        // Fetch fresh order data from server
        const freshOrderRes = await axios.get(
          `${API_BASE}/materialOrders2/${orderId}`,
          { headers },
        );
        const freshOrder = freshOrderRes.data;

        // Update local state
        setOrder(freshOrder);

        // Refresh email form with fresh contact data
        const contactEmails = contacts
          .filter((c) =>
            (Array.isArray(freshOrder.contact)
              ? freshOrder.contact.map((ct) => ct._id || ct)
              : [freshOrder.contact?._id || freshOrder.contact]
            ).includes(c._id),
          )
          .map((c) => c.email)
          .filter((email) => email)
          .join(", ");

        const primaryContact = contacts.find((c) =>
          (Array.isArray(freshOrder.contact)
            ? freshOrder.contact.map((ct) => ct._id || ct)
            : [freshOrder.contact?._id || freshOrder.contact]
          ).includes(c._id),
        );

        const greeting = primaryContact?.firstName
          ? `Hi ${primaryContact.firstName},`
          : `Dear Vendor,`;

        setEmailForm((prev) => ({
          ...prev,
          to: contactEmails || prev.to,
          subject: `${project.projectName} - PO#${project.projectNumber}-${freshOrder.materialOrderNumber}`,
          body: `${greeting}\n\nPlease find attached the material order for your review and action.\n\nBest regards,`,
        }));

        setIsSaving(false);

        // Delay to ensure state update completes
        setTimeout(() => {
          generatePDF(true);
        }, 100);
      } catch (err) {
        console.error("Pre-email save error:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Save Failed",
          message: `Failed to save changes before sending email: ${err.response?.data?.message || err.message}`,
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        setIsSaving(false);
        return;
      }
    } else {
      generatePDF(true);
    }
  };
  const handleEmailSubmit = async () => {
    const safeProjectName = (project?.projectName || "Project")
      .replace(/[^a-zA-Z0-9-_ ]/g, "")
      .replace(/\s+/g, " ")
      .trim();

    try {
      if (!emailForm.to || !emailForm.subject || !emailForm.body) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Input",
          message: "Please fill in all required email fields",
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      const validateEmails = (emails) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails
          .split(",")
          .map((email) => email.trim())
          .every((email) => emailRegex.test(email));
      };

      if (!validateEmails(emailForm.to)) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Email",
          message: "Please enter valid email addresses",
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      if (
        !(vendorPdfBuffer instanceof Blob) ||
        vendorPdfBuffer.type !== "application/pdf"
      ) {
        setAlert({
          open: true,
          type: "error",
          title: "Missing Attachment",
          message: "Vendor PDF is missing or invalid. Please regenerate.",
          confirmText: "OK",
          onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("to", emailForm.to);
      formData.append("subject", emailForm.subject);
      formData.append("body", emailForm.body);
      formData.append("signature", emailForm.signature || "");
      formData.append("from", userEmail);

      // Vendor PDF → fieldname "files"
      formData.append(
        "files",
        vendorPdfBuffer,
        `MaterialOrder-${order.materialOrderNumber} - ${safeProjectName}.pdf`,
      );

      // Site PDF → fieldname "siteFiles"
      if (sitePdfBuffer instanceof Blob) {
        formData.append(
          "siteFiles",
          sitePdfBuffer,
          `MaterialOrder-${order.materialOrderNumber} - ${safeProjectName} - SITE.pdf`,
        );
      }

      const response = await axios.post(
        `${API_BASE}/materialOrders2/${orderId}/send-email`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to send email");
      }

      setShowEmailForm(false);
      setVendorPdfBuffer(null);
      setSitePdfBuffer(null);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Vendor email and internal site copy sent!",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message || "Unknown error",
        confirmText: "OK",
        onConfirm: () => setAlert((prev) => ({ ...prev, open: false })),
      });
      console.error("Email error:", err);
    }
  };

  const isEditable =
    order?.status === "pending" || order?.status === "confirmed";

  const canEditSurcharges = true;
  const isDelivered = order?.status === "delivered";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800">
        <div className="text-gray-200">Loading...</div>
      </div>
    );
  }

  if (!order || !project) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800">
        <div className="text-gray-200">No data available</div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        onClose={alert.onClose}
        onConfirm={alert.onConfirm}
      />
      <div className="flex gap-6">
        <div
          className={`flex-1 ${
            showVariancePanel && varianceSummary.hasChanges && isEditable
              ? "max-w-[70%]"
              : ""
          }`}
        >
          <div className="bg-gray-700 rounded-xl border border-gray-600 shadow-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-800 border-b border-gray-600 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-white">
                  MO# {project.projectNumber} -{order.materialOrderNumber}
                </h1>
                {getStatusBadge(order.status)}
                {order.hasVariance && (
                  <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-xs font-medium">
                    Variance Applied
                  </span>
                )}
              </div>
              <button
                onClick={() =>
                  navigate(`/projects/${id}`, {
                    state: { initialTab: "Materials" },
                  })
                }
                className="px-3 py-1.5 text-sm font-medium text-gray-300 bg-gray-700 rounded hover:bg-gray-600 transition-colors"
              >
                ← Back
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Purchase Order
                  </label>
                  <div className="text-sm text-gray-200">
                    {po?.title || order.poNumber} - PO# {order.poNumber}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Vendor
                  </label>
                  <div className="text-sm text-gray-200">
                    {vendors.find(
                      (v) => v._id === (order.vendor?._id || order.vendor),
                    )?.companyName || "Unknown"}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Revision
                  </label>
                  {isEditable ? (
                    <NumericFormat
                      value={formData.revision}
                      onValueChange={(values) =>
                        handleFormChange("revision", values.floatValue || 0)
                      }
                      allowNegative={false}
                      decimalScale={0}
                      className="w-16 px-2 py-1 text-sm rounded border-gray-500 bg-gray-600 text-gray-200"
                    />
                  ) : (
                    <div className="text-sm text-gray-200">
                      R{formData.revision}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Expected Delivery
                  </label>
                  {isEditable ? (
                    <input
                      type="date"
                      value={formData.expectedDeliveryDate}
                      onChange={(e) =>
                        handleFormChange("expectedDeliveryDate", e.target.value)
                      }
                      className="w-full px-2 py-1 text-sm rounded border-gray-500 bg-gray-600 text-gray-200"
                    />
                  ) : (
                    <div className="text-sm text-gray-200">
                      {formData.expectedDeliveryDate || "Not set"}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-600">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Shipping Method
                  </label>
                  {isEditable ? (
                    <select
                      value={formData.shippingMethod}
                      onChange={(e) =>
                        handleFormChange("shippingMethod", e.target.value)
                      }
                      className="w-full px-2 py-1.5 text-sm rounded border border-gray-500 bg-gray-600 text-gray-200"
                    >
                      <option value="Delivery">Delivery</option>
                      <option value="Pickup">Pickup</option>
                    </select>
                  ) : (
                    <div className="text-sm text-gray-200">
                      {formData.shippingMethod}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Vendor Contacts
                  </label>
                  <Select
                    options={contactOptions}
                    value={contactOptions.filter((o) =>
                      formData.contact.includes(o.value),
                    )}
                    onChange={(selected) =>
                      handleFormChange(
                        "contact",
                        selected ? selected.map((o) => o.value) : [],
                      )
                    }
                    isMulti
                    placeholder="Select..."
                    styles={customStyles}
                    isDisabled={!isEditable}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Field Contacts
                  </label>
                  <Select
                    options={userOptions}
                    value={userOptions.filter((o) =>
                      formData.fieldContact.includes(o.value),
                    )}
                    onChange={(selected) =>
                      handleFormChange(
                        "fieldContact",
                        selected ? selected.map((o) => o.value) : [],
                      )
                    }
                    isMulti
                    placeholder="Select..."
                    styles={customStyles}
                    isDisabled={!isEditable}
                  />
                </div>
              </div>

              {/* Delivery Driver row — only shown for Pickup */}
              {formData.shippingMethod === "Pickup" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Delivery Driver
                    </label>
                    <Select
                      options={userOptions}
                      value={
                        userOptions.find(
                          (o) => o.value === formData.deliveryContact,
                        ) || null
                      }
                      onChange={(opt) =>
                        handleFormChange("deliveryContact", opt?.value || null)
                      }
                      placeholder="Select Driver..."
                      styles={customStyles}
                      isClearable
                      isDisabled={!isEditable}
                    />
                  </div>
                </div>
              )}
              {isEditable && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Delivery Instructions
                  </label>
                  <textarea
                    value={formData.deliveryInstructions}
                    onChange={(e) =>
                      handleFormChange("deliveryInstructions", e.target.value)
                    }
                    rows={2}
                    className="w-full px-3 py-2 text-sm rounded border-gray-500 bg-gray-600 text-gray-200"
                    placeholder="Enter any special instructions..."
                  />
                </div>
              )}
              <div className="pt-4 border-t border-gray-600">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-base font-semibold text-white">
                    Order Items
                  </h3>
                  <div className="flex items-center gap-2">
                    {varianceSummary.hasChanges && (
                      <span className="text-xs text-orange-400 bg-orange-500/10 px-2 py-1 rounded">
                        {varianceSummary.changes.length} variance(s)
                      </span>
                    )}
                    {isEditable && availablePOItems.length > 0 && (
                      <button
                        onClick={() => {
                          setShowAddFromPO(!showAddFromPO);
                          setShowAddNewItem(false);
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                          showAddFromPO
                            ? "bg-blue-600 text-white"
                            : "bg-gray-600 text-gray-200 hover:bg-gray-500"
                        }`}
                      >
                        {showAddFromPO ? "Hide PO Items" : "+ From PO"}
                      </button>
                    )}
                  </div>
                </div>
                {showAddFromPO && isEditable && (
                  <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-blue-500/30">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-blue-400">
                        Add from PO (same vendor) - not a variance
                      </span>
                      <input
                        type="text"
                        value={addItemSearch}
                        onChange={(e) => setAddItemSearch(e.target.value)}
                        placeholder="Search..."
                        className="px-2 py-1 text-xs rounded border-gray-500 bg-gray-600 text-gray-200 w-40"
                      />
                    </div>
                    <div className="max-h-36 overflow-y-auto">
                      {filteredPOItems.length === 0 ? (
                        <p className="text-gray-500 text-xs py-2">
                          No items available from PO
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {filteredPOItems.map((item) => (
                            <div
                              key={item.lineItemId}
                              className="flex items-center justify-between py-1.5 px-2 bg-gray-700/50 rounded hover:bg-gray-700 transition-colors"
                            >
                              <div className="flex-1 min-w-0 mr-3">
                                <div className="text-xs text-gray-200 truncate">
                                  {item.description || "Unnamed Item"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.size} • {item.uom} •{" "}
                                  {item.isFullyOrdered ? (
                                    <span className="text-orange-400 font-medium">
                                      Fully ordered — will create variance
                                    </span>
                                  ) : (
                                    <>Avail: {item.remainingQty}</>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddFromPO(item)}
                                className="px-2 py-0.5 text-xs font-medium text-blue-400 border border-blue-400 rounded hover:bg-blue-400/10"
                              >
                                Add
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* {showAddNewItem && isEditable && (
                  <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-orange-500/30">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-orange-400">
                        Add New Item (creates variance)
                      </span>
                      <input
                        type="text"
                        value={newItemSearch}
                        onChange={(e) => setNewItemSearch(e.target.value)}
                        placeholder="Search budget items..."
                        className="px-2 py-1 text-xs rounded border-gray-500 bg-gray-600 text-gray-200 w-40"
                      />
                    </div>
                    <div className="max-h-36 overflow-y-auto">
                      {filteredBudgetItems.length === 0 ? (
                        <p className="text-gray-500 text-xs py-2">
                          No budget items available
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {filteredBudgetItems.map((item) => (
                            <div
                              key={item.lineItemId}
                              className="flex items-center justify-between py-1.5 px-2 bg-gray-700/50 rounded hover:bg-gray-700 transition-colors"
                            >
                              <div className="flex-1 min-w-0 mr-3">
                                <div className="text-xs text-gray-200 truncate">
                                  {item.description || "Unnamed Item"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.conditionName} • {item.size} •{" "}
                                  {item.uom}
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddNewItem(item)}
                                className="px-2 py-0.5 text-xs font-medium text-orange-400 border border-orange-400 rounded hover:bg-orange-400/10"
                              >
                                Add
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )} */}
                <div className="rounded-lg border border-gray-600 overflow-hidden">
                  <div className="bg-gray-800 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-4 text-xl">
                        <span className="text-gray-400">
                          <span className="text-white font-semibold">
                            {groupByAssembly
                              ? groupedItems.length.toLocaleString()
                              : filteredEditableItems.length.toLocaleString()}
                          </span>
                          {filteredEditableItems.length !==
                            editableItems.length && (
                            <span className="text-gray-500">
                              {" "}
                              / {editableItems.length.toLocaleString()}
                            </span>
                          )}{" "}
                          items
                        </span>
                        <span className="text-gray-600">|</span>
                        <span className="text-orange-400 font-medium">
                          {formatCurrency(filteredTotals.itemsTotal)}
                        </span>
                        <span className="text-gray-600">|</span>
                        <span className="text-blue-400">
                          {filteredTotals.totalQuantity.toFixed(0)} qty ordered
                        </span>
                      </div>

                      {/* Group Toggle */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-300 whitespace-nowrap">
                          Group by Assembly
                        </label>
                        <div
                          className={`relative inline-flex h-6 w-11 cursor-pointer items-center rounded-full transition-colors ${
                            groupByAssembly ? "bg-orange-600" : "bg-gray-600"
                          }`}
                          onClick={() => setGroupByAssembly(!groupByAssembly)}
                        >
                          <span
                            className={`absolute left-1 top-1 block h-4 w-4 rounded-full bg-white transition-transform ${
                              groupByAssembly
                                ? "translate-x-5"
                                : "translate-x-0"
                            }`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Filter Badges Row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Search Input */}
                      <div className="relative">
                        <input
                          type="text"
                          value={tableFilters.descriptionSearch}
                          onChange={(e) =>
                            handleTableFilterChange(
                              "descriptionSearch",
                              e.target.value,
                            )
                          }
                          placeholder="Search..."
                          className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                        />
                        {tableFilters.descriptionSearch && (
                          <button
                            onClick={() =>
                              handleTableFilterChange("descriptionSearch", "")
                            }
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <span className="text-gray-600">|</span>

                      {/* Active Filter Chips */}
                      {tableFilters.conditionName.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                          Condition:{" "}
                          {tableFilters.conditionName.length === 1
                            ? tableFilters.conditionName[0].substring(0, 15) +
                              (tableFilters.conditionName[0].length > 15
                                ? "…"
                                : "")
                            : `${tableFilters.conditionName.length} selected`}
                          <button
                            onClick={() =>
                              handleTableFilterChange("conditionName", [])
                            }
                            className="hover:text-teal-200"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {tableFilters.size.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-cyan-600/80 text-white rounded">
                          Size:{" "}
                          {tableFilters.size.length === 1
                            ? tableFilters.size[0]
                            : `${tableFilters.size.length} selected`}
                          <button
                            onClick={() => handleTableFilterChange("size", [])}
                            className="hover:text-cyan-200"
                          >
                            ×
                          </button>
                        </span>
                      )}

                      {tableFilters.sectionNumber.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                          Section:{" "}
                          {tableFilters.sectionNumber.length === 1
                            ? tableFilters.sectionNumber[0]
                            : `${tableFilters.sectionNumber.length} selected`}
                          <button
                            onClick={() =>
                              handleTableFilterChange("sectionNumber", [])
                            }
                            className="hover:text-purple-200"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {tableFilters.bidArea.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                          Bid Area:{" "}
                          {tableFilters.bidArea.length === 1
                            ? tableFilters.bidArea[0]
                            : `${tableFilters.bidArea.length} selected`}
                          <button
                            onClick={() =>
                              handleTableFilterChange("bidArea", [])
                            }
                            className="hover:text-green-200"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {tableFilters.uom.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                          UOM:{" "}
                          {tableFilters.uom.length === 1
                            ? tableFilters.uom[0]
                            : `${tableFilters.uom.length} selected`}
                          <button
                            onClick={() => handleTableFilterChange("uom", [])}
                            className="hover:text-indigo-200"
                          >
                            ×
                          </button>
                        </span>
                      )}

                      {/* Clear All */}
                      {hasActiveFilters && (
                        <button
                          onClick={clearAllTableFilters}
                          className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                  <table className="min-w-full">
                    <thead className="bg-gray-800">
                      <tr>
                        {/* Condition */}
                        <th
                          className={`px-3 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                            tableFilters.conditionName.length > 0
                              ? "text-orange-400"
                              : "text-gray-400"
                          }`}
                          onClick={() => toggleTableFilter("conditionName")}
                        >
                          <span className="flex items-center gap-0.5">
                            Condition
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            {tableFilters.conditionName.length > 0 && (
                              <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                                {tableFilters.conditionName.length}
                              </span>
                            )}
                          </span>
                          {showTableFilter === "conditionName" && (
                            <div
                              ref={tableConditionRef}
                              className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                              style={{ minWidth: "300px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-3">
                                <Select
                                  isMulti
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                  options={tableFilterOptions.conditionName.map(
                                    (v) => ({ value: v, label: v }),
                                  )}
                                  value={tableFilterOptions.conditionName
                                    .map((v) => ({ value: v, label: v }))
                                    .filter((opt) =>
                                      tableFilters.conditionName.includes(
                                        opt.value,
                                      ),
                                    )}
                                  onChange={(opts) =>
                                    handleTableFilterChange(
                                      "conditionName",
                                      opts ? opts.map((o) => o.value) : [],
                                    )
                                  }
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={multiSelectFilterStyles}
                                />
                              </div>
                            </div>
                          )}
                        </th>

                        {/* Section */}
                        <th
                          className={`px-3 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                            tableFilters.sectionNumber.length > 0
                              ? "text-orange-400"
                              : "text-gray-400"
                          }`}
                          onClick={() => toggleTableFilter("sectionNumber")}
                        >
                          <span className="flex items-center gap-0.5">
                            Section
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            {tableFilters.sectionNumber.length > 0 && (
                              <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                                {tableFilters.sectionNumber.length}
                              </span>
                            )}
                          </span>
                          {showTableFilter === "sectionNumber" && (
                            <div
                              ref={tableSectionRef}
                              className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                              style={{ minWidth: "220px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-3">
                                <Select
                                  isMulti
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                  options={tableFilterOptions.sectionNumber.map(
                                    (v) => ({ value: v, label: v }),
                                  )}
                                  value={tableFilterOptions.sectionNumber
                                    .map((v) => ({ value: v, label: v }))
                                    .filter((opt) =>
                                      tableFilters.sectionNumber.includes(
                                        opt.value,
                                      ),
                                    )}
                                  onChange={(opts) =>
                                    handleTableFilterChange(
                                      "sectionNumber",
                                      opts ? opts.map((o) => o.value) : [],
                                    )
                                  }
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={multiSelectFilterStyles}
                                />
                              </div>
                            </div>
                          )}
                        </th>

                        {/* Labor ID */}
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                          Labor ID
                        </th>

                        {/* Description */}
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase min-w-[180px]">
                          Description
                        </th>

                        {/* Size */}
                        <th
                          className={`px-3 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                            tableFilters.size.length > 0
                              ? "text-orange-400"
                              : "text-gray-400"
                          }`}
                          onClick={() => toggleTableFilter("size")}
                        >
                          <span className="flex items-center gap-0.5">
                            Size
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            {tableFilters.size.length > 0 && (
                              <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                                {tableFilters.size.length}
                              </span>
                            )}
                          </span>
                          {showTableFilter === "size" && (
                            <div
                              ref={tableSizeRef}
                              className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                              style={{ minWidth: "220px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-3">
                                {/* Show a divider label if we have "in filter" sizes */}
                                {tableFilterOptions.sizeOptions.some(
                                  (o) => o.inFilter,
                                ) && (
                                  <div className="mb-1 px-2 py-1 text-[10px] font-semibold text-orange-400 uppercase tracking-wide">
                                    In current filter
                                  </div>
                                )}
                                <Select
                                  isMulti
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                  options={tableFilterOptions.sizeOptions.map(
                                    (o) => ({
                                      value: o.value,
                                      label: o.label,
                                      ...(o.inFilter ? {} : {}),
                                    }),
                                  )}
                                  formatOptionLabel={(opt) => {
                                    const isInFilter =
                                      tableFilterOptions.sizeOptions.find(
                                        (o) => o.value === opt.value,
                                      )?.inFilter;
                                    // Show divider before first non-filter item
                                    return (
                                      <div className="flex items-center gap-2">
                                        {isInFilter && (
                                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                                        )}
                                        <span>{opt.label}</span>
                                      </div>
                                    );
                                  }}
                                  value={tableFilterOptions.sizeOptions
                                    .filter((o) =>
                                      tableFilters.size.includes(o.value),
                                    )
                                    .map((o) => ({
                                      value: o.value,
                                      label: o.label,
                                    }))}
                                  onChange={(opts) =>
                                    handleTableFilterChange(
                                      "size",
                                      opts ? opts.map((o) => o.value) : [],
                                    )
                                  }
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={{
                                    ...multiSelectFilterStyles,
                                    option: (base, state) => ({
                                      ...base,
                                      backgroundColor: state.isFocused
                                        ? "#4b5563"
                                        : "#374151",
                                      color: state.isSelected
                                        ? "#f97316"
                                        : "#e5e7eb",
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      fontSize: "13px",
                                      borderTop: (() => {
                                        const opts =
                                          tableFilterOptions.sizeOptions;
                                        const idx = opts.findIndex(
                                          (o) => o.value === state.data.value,
                                        );
                                        // Draw separator before the first non-filter item
                                        if (
                                          idx > 0 &&
                                          !opts[idx].inFilter &&
                                          opts[idx - 1].inFilter
                                        ) {
                                          return "1px solid #4b5563";
                                        }
                                        return "none";
                                      })(),
                                      paddingTop: (() => {
                                        const opts =
                                          tableFilterOptions.sizeOptions;
                                        const idx = opts.findIndex(
                                          (o) => o.value === state.data.value,
                                        );
                                        if (
                                          idx > 0 &&
                                          !opts[idx].inFilter &&
                                          opts[idx - 1].inFilter
                                        ) {
                                          return "10px";
                                        }
                                        return "8px";
                                      })(),
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </th>

                        {/* Qty */}
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                          Qty
                        </th>

                        {/* UoM */}
                        <th
                          className={`px-3 py-2 text-center text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                            tableFilters.uom.length > 0
                              ? "text-orange-400"
                              : "text-gray-400"
                          }`}
                          onClick={() => toggleTableFilter("uom")}
                        >
                          <span className="flex items-center justify-center gap-0.5">
                            Unit
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            {tableFilters.uom.length > 0 && (
                              <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                                {tableFilters.uom.length}
                              </span>
                            )}
                          </span>
                          {showTableFilter === "uom" && (
                            <div
                              ref={tableUomRef}
                              className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                              style={{ minWidth: "180px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-3">
                                <Select
                                  isMulti
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                  options={tableFilterOptions.uom.map((v) => ({
                                    value: v,
                                    label: v,
                                  }))}
                                  value={tableFilterOptions.uom
                                    .map((v) => ({ value: v, label: v }))
                                    .filter((opt) =>
                                      tableFilters.uom.includes(opt.value),
                                    )}
                                  onChange={(opts) =>
                                    handleTableFilterChange(
                                      "uom",
                                      opts ? opts.map((o) => o.value) : [],
                                    )
                                  }
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={multiSelectFilterStyles}
                                />
                              </div>
                            </div>
                          )}
                        </th>

                        {/* Bid Area */}
                        <th
                          className={`px-3 py-2 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 uppercase ${
                            tableFilters.bidArea.length > 0
                              ? "text-orange-400"
                              : "text-gray-400"
                          }`}
                          onClick={() => toggleTableFilter("bidArea")}
                        >
                          <span className="flex items-center gap-0.5">
                            Bid Area
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                            {tableFilters.bidArea.length > 0 && (
                              <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                                {tableFilters.bidArea.length}
                              </span>
                            )}
                          </span>
                          {showTableFilter === "bidArea" && (
                            <div
                              ref={tableBidAreaRef}
                              className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                              style={{ minWidth: "220px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-3">
                                <Select
                                  isMulti
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                  options={tableFilterOptions.bidArea.map(
                                    (v) => ({ value: v, label: v }),
                                  )}
                                  value={tableFilterOptions.bidArea
                                    .map((v) => ({ value: v, label: v }))
                                    .filter((opt) =>
                                      tableFilters.bidArea.includes(opt.value),
                                    )}
                                  onChange={(opts) =>
                                    handleTableFilterChange(
                                      "bidArea",
                                      opts ? opts.map((o) => o.value) : [],
                                    )
                                  }
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={multiSelectFilterStyles}
                                />
                              </div>
                            </div>
                          )}
                        </th>

                        {userRole !== "foreman" && (
                          <>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Unit Cost
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Pkg Cost
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                              Total
                            </th>
                          </>
                        )}

                        {isEditable && (
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-400 uppercase w-20" />
                        )}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-600 bg-gray-700">
                      {displayTableItems.map((item) => {
                        const isGroup = item._isGroup === true;
                        const isClosedOut = item.isClosedOut;
                        const isEditing =
                          !isGroup && editingItemId === item.lineItemId;

                        // For grouped items, use aggregated values
                        const displayQty = isGroup
                          ? item._totalQuantity
                          : safeNumber(item.quantity, 0);
                        const displayTotal = isGroup
                          ? item._totalLineTotal
                          : calculateLineTotal(item);

                        const currPrice = safeNumber(item.price, 0);
                        const origPrice = safeNumber(item.originalPrice, 0);
                        const remainingAvailable = isGroup
                          ? item._totalRemaining
                          : safeNumber(item.remainingAvailable, 0);
                        const qtyOrdered = isGroup
                          ? item._totalOrdered
                          : safeNumber(item.qtyOrdered, 0);
                        const netRequiredQty = isGroup
                          ? item._totalNetRequired
                          : safeNumber(item.netRequiredQty, 0);

                        const isOriginalItem =
                          !item.isVarianceItem && item.originalSize;
                        const sizeChanged =
                          item.size !== item.originalSize &&
                          item.originalSize !== "";
                        const priceChanged = currPrice !== origPrice;
                        const exceedsRemaining =
                          !isGroup &&
                          !isDelivered &&
                          displayQty > remainingAvailable;

                        const hasVarianceChange =
                          item.isVarianceItem ||
                          exceedsRemaining ||
                          (isOriginalItem && (sizeChanged || priceChanged));

                        const wasAddedFromPO =
                          !item.isVarianceItem && !item.originalSize;
                        const canRemove =
                          item.isAddedFromPO ||
                          item.isVarianceItem ||
                          (!item.isVarianceItem && !item.isClosedOut);

                        return (
                          <tr
                            key={isGroup ? item._groupKey : item.lineItemId}
                            className={`
          ${isClosedOut ? "bg-red-900/20 opacity-70" : hasVarianceChange ? "bg-orange-500/5" : ""}
          ${wasAddedFromPO || item.isAddedFromPO ? "bg-blue-500/5" : ""}
          ${isEditing && !isClosedOut ? "bg-gray-600/30" : ""}
          ${exceedsRemaining && !isClosedOut ? "bg-red-500/10" : ""}
          ${isGroup ? "bg-gray-800/50" : ""}
        `}
                          >
                            {/* Condition */}
                            <td className="px-3 py-2 text-xs text-gray-300">
                              <div className="flex flex-col">
                                <span>{item.conditionName || "-"}</span>
                                {isGroup && (
                                  <span className="text-[10px] text-gray-500 mt-0.5">
                                    ({item._count} items)
                                  </span>
                                )}
                              </div>
                              {isClosedOut && (
                                <div className="mt-1">
                                  <span className="inline-block px-2 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded">
                                    CLOSED OUT
                                  </span>
                                </div>
                              )}
                            </td>

                            {/* Section */}
                            <td className="px-3 py-2 text-xs text-gray-300">
                              {isGroup ? "—" : item.sectionNumber || "—"}
                            </td>

                            {/* Labor ID */}
                            <td className="px-3 py-2 text-xs text-gray-300">
                              {isGroup ? "—" : item.laborId || "—"}
                            </td>

                            {/* Description */}
                            <td className="px-3 py-2 text-xs text-gray-200 max-w-[180px]">
                              <div
                                className="truncate"
                                title={item.description}
                              >
                                {item.description || "-"}
                              </div>
                              {isClosedOut && (
                                <div className="text-[10px] text-red-400 mt-1 font-medium">
                                  Size changed on other order — no further edits
                                  allowed
                                </div>
                              )}
                              {!isGroup && item.isVarianceItem && (
                                <span className="inline-block mt-0.5 px-1 py-0.5 text-[10px] bg-orange-500/20 text-orange-400 rounded">
                                  VAR
                                </span>
                              )}
                              {!isGroup &&
                                (item.isAddedFromPO || wasAddedFromPO) && (
                                  <span className="inline-block mt-0.5 px-1 py-0.5 text-[10px] bg-blue-500/20 text-blue-400 rounded">
                                    +PO
                                  </span>
                                )}
                            </td>

                            {/* Size */}
                            <td className="px-3 py-2 text-xs">
                              {isGroup ? (
                                <span className="text-gray-400">
                                  {item.size || "-"}
                                </span>
                              ) : isEditing ? (
                                <input
                                  type="text"
                                  value={item.size}
                                  onChange={(e) => {
                                    const newSize = e.target.value;
                                    handleItemChange(
                                      item.lineItemId,
                                      "size",
                                      newSize,
                                    );
                                    handleSizeChangeWithAutoQty(
                                      item.lineItemId,
                                      newSize,
                                    );
                                  }}
                                  className={`w-28 px-2 py-1 text-xs rounded border bg-gray-600 text-gray-200 ${
                                    item.sizeChangedByForeman
                                      ? "border-orange-500"
                                      : "border-gray-500"
                                  }`}
                                  placeholder="4'x10'"
                                />
                              ) : (
                                <span
                                  className={
                                    item.sizeChangedByForeman
                                      ? "text-orange-300"
                                      : "text-gray-200"
                                  }
                                >
                                  {item.size || "-"}
                                </span>
                              )}
                              {!isGroup && item.sizeChangedByForeman && (
                                <div className="flex items-center gap-1 mt-1">
                                  <span className="text-[10px] text-orange-400">
                                    was: {item.originalSize}
                                  </span>
                                  <span className="px-1 py-0.5 bg-orange-500/20 text-orange-400 text-[9px] rounded">
                                    FOREMAN
                                  </span>
                                </div>
                              )}
                              {!isGroup &&
                                sizeChanged &&
                                isOriginalItem &&
                                !item.sizeChangedByForeman && (
                                  <div className="text-[10px] text-orange-400 mt-1">
                                    was: {item.originalSize}
                                  </div>
                                )}
                            </td>

                            {/* Qty */}
                            <td className="px-3 py-2 text-xs text-right">
                              {isGroup ? (
                                <span className="text-gray-200 font-medium">
                                  {displayQty.toFixed(0)}
                                </span>
                              ) : isEditing ? (
                                <NumericFormat
                                  value={item.quantity}
                                  onValueChange={(values) =>
                                    handleItemChange(
                                      item.lineItemId,
                                      "quantity",
                                      values.floatValue || 0,
                                    )
                                  }
                                  allowNegative={false}
                                  decimalScale={0}
                                  className={`w-16 px-1.5 py-0.5 text-xs text-right rounded border-gray-500 bg-gray-600 text-gray-200 ${
                                    exceedsRemaining
                                      ? "border-red-500 bg-red-500/20"
                                      : ""
                                  }`}
                                />
                              ) : (
                                <span
                                  className={
                                    exceedsRemaining
                                      ? "text-red-400"
                                      : "text-gray-200"
                                  }
                                >
                                  {displayQty}
                                </span>
                              )}
                              {!isDelivered && !isGroup && (
                                <>
                                  {qtyOrdered > 0 && (
                                    <div className="text-[10px] text-yellow-400">
                                      ordered: {qtyOrdered}
                                    </div>
                                  )}
                                  <div className="text-[10px] text-blue-400">
                                    budget: {netRequiredQty}
                                  </div>
                                  <div
                                    className={`text-[10px] ${
                                      exceedsRemaining
                                        ? "text-red-400 font-medium"
                                        : item.showMaxAsUnlimited
                                          ? "text-orange-400"
                                          : "text-gray-500"
                                    }`}
                                  >
                                    avail:{" "}
                                    {item.showMaxAsUnlimited
                                      ? "∞ (var)"
                                      : remainingAvailable}
                                    {exceedsRemaining && " ⚠"}
                                  </div>
                                  {item.isOverCommitted &&
                                    !item.showMaxAsUnlimited && (
                                      <div className="text-[10px] text-amber-400 font-medium">
                                        ⚠ committed: {item.totalCommitted}/
                                        {netRequiredQty}
                                      </div>
                                    )}
                                </>
                              )}
                            </td>

                            {/* Unit (UoM) */}
                            <td className="px-3 py-2 text-xs text-center text-gray-300">
                              {isGroup
                                ? isSFUnit(item.uom) || isLFUnit(item.uom)
                                  ? "PC"
                                  : item.uom || "—"
                                : isSFUnit(item.uom) || isLFUnit(item.uom)
                                  ? "PC"
                                  : item.uom || "—"}
                            </td>

                            {/* Bid Area */}
                            <td className="px-3 py-2 text-xs text-gray-300">
                              {isGroup
                                ? item.bidArea || "—"
                                : item.bidArea || "—"}
                            </td>

                            {userRole !== "foreman" && (
                              <>
                                {/* Unit Cost */}
                                <td className="px-3 py-2 text-xs text-right">
                                  {isGroup ? (
                                    <span className="text-gray-200">
                                      ${formatCurrency(item.price)}
                                    </span>
                                  ) : isEditing ? (
                                    <NumericFormat
                                      value={item.price}
                                      onValueChange={(values) =>
                                        handleItemChange(
                                          item.lineItemId,
                                          "price",
                                          values.floatValue || 0,
                                        )
                                      }
                                      allowNegative={false}
                                      decimalScale={2}
                                      prefix="$"
                                      className="w-20 px-1.5 py-0.5 text-xs text-right rounded border-gray-500 bg-gray-600 text-gray-200"
                                    />
                                  ) : (
                                    <span className="text-gray-200">
                                      ${formatCurrency(currPrice)}
                                    </span>
                                  )}
                                  {!isGroup &&
                                    isOriginalItem &&
                                    priceChanged && (
                                      <div className="text-[10px] text-orange-400">
                                        was: ${formatCurrency(origPrice)}
                                      </div>
                                    )}
                                </td>

                                {/* Pkg Cost */}
                                <td className="px-3 py-2 text-xs text-right">
                                  {(() => {
                                    const isCoverage =
                                      isSFUnit(item.uom) || isLFUnit(item.uom);
                                    const coverage = isGroup
                                      ? getSafeCoverage(item.coveragePerUnit)
                                      : getCoverage(item);
                                    const pkgCost = isCoverage
                                      ? (isGroup ? item.price : currPrice) *
                                        coverage
                                      : isGroup
                                        ? item.price
                                        : currPrice;
                                    return (
                                      <>
                                        <span className="font-mono font-medium text-orange-300">
                                          ${formatCurrency(pkgCost)}
                                        </span>
                                        {isCoverage && (
                                          <div className="text-[10px] text-gray-500 text-right">
                                            ×{coverage} {item.uom}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </td>

                                {/* Total */}
                                <td className="px-3 py-2 text-xs text-right text-gray-200 font-medium">
                                  ${formatCurrency(displayTotal)}
                                </td>
                              </>
                            )}

                            {/* Actions */}
                            {isEditable && (
                              <td className="px-3 py-2 text-center">
                                {isGroup ? (
                                  <span className="text-[10px] text-gray-500 italic">
                                    (grouped)
                                  </span>
                                ) : isClosedOut ? (
                                  <span className="text-[10px] text-red-400 font-medium">
                                    Locked
                                  </span>
                                ) : (
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() =>
                                        toggleEditItem(item.lineItemId)
                                      }
                                      className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                                        isEditing
                                          ? "bg-green-500/20 text-green-400"
                                          : "bg-gray-600 text-gray-400 hover:text-white"
                                      }`}
                                    >
                                      {isEditing ? "Done" : "Edit"}
                                    </button>
                                    {canRemove && (
                                      <button
                                        onClick={() =>
                                          handleRemoveItem(item.lineItemId)
                                        }
                                        className="px-1.5 py-0.5 text-[15px] text-red-400 hover:text-red-300"
                                      >
                                        {FiTrash()}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}

                      {/* Empty state */}
                      {(groupByAssembly ? groupedItems : filteredEditableItems)
                        .length === 0 && (
                        <tr>
                          <td
                            colSpan={isEditable ? 8 : 7}
                            className="px-4 py-6 text-center text-gray-500 text-sm"
                          >
                            {hasActiveFilters
                              ? "No items matching filters"
                              : "No items on this order"}
                          </td>
                        </tr>
                      )}
                      {(groupByAssembly ? groupedItems : filteredEditableItems)
                        .length > 0 && (
                        <tr className="bg-gray-800/90 font-bold border-t-2 border-gray-500 sticky bottom-0 z-10">
                          <td
                            className="px-3 py-3 text-right text-orange-300"
                            colSpan={4}
                          >
                            TOTAL (
                            {groupByAssembly
                              ? groupedItems.length
                              : filteredEditableItems.length}{" "}
                            {groupByAssembly
                              ? "unique items"
                              : "assembly items"}
                            )
                          </td>
                          <td className="px-3 py-3" />
                          {/* size */}
                          <td className="px-3 py-3 text-right text-orange-400 font-bold">
                            {filteredTotals.totalQuantity.toFixed(0)}
                          </td>
                          <td className="px-3 py-3" />
                          {/* unit */}
                          <td className="px-3 py-3" />
                          {/* bid area */}
                          {userRole !== "foreman" && (
                            <>
                              <td className="px-3 py-3" />
                              {/* unit cost */}
                              <td className="px-3 py-3" />
                              {/* pkg cost */}
                              <td className="px-3 py-3 text-right text-orange-400 font-bold">
                                ${formatCurrency(filteredTotals.itemsTotal)}
                              </td>
                            </>
                          )}
                          {isEditable && <td className="px-3 py-3" />}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-600">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-white">
                    Surcharges
                  </h3>
                  {canEditSurcharges && (
                    <button
                      onClick={handleAddSurcharge}
                      className="px-2 py-1 text-xs font-medium text-gray-300 bg-gray-600 rounded hover:bg-gray-500"
                    >
                      + Add
                    </button>
                  )}
                </div>
                {surcharges.length > 0 ? (
                  <div className="space-y-1">
                    {surcharges.map((surcharge, index) => (
                      <div key={index} className="flex items-center gap-2 py-1">
                        <input
                          type="text"
                          value={surcharge.description}
                          onChange={(e) =>
                            handleSurchargeChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="Description"
                          className="flex-1 px-2 py-1 text-xs rounded border-gray-500 bg-gray-600 text-gray-200"
                          disabled={!canEditSurcharges}
                        />
                        <NumericFormat
                          value={surcharge.price}
                          onValueChange={(values) =>
                            handleSurchargeChange(
                              index,
                              "price",
                              values.floatValue || 0,
                            )
                          }
                          allowNegative={true}
                          decimalScale={2}
                          prefix="$"
                          className="w-24 px-2 py-1 text-xs text-right rounded border-gray-500 bg-gray-600 text-gray-200"
                          disabled={!canEditSurcharges}
                        />
                        {canEditSurcharges && (
                          <button
                            onClick={() => handleRemoveSurcharge(index)}
                            className="text-red-400 hover:text-red-300 text-xs px-1"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No surcharges</p>
                )}
              </div>
              {userRole !== "foreman" && (
                <div className="flex justify-between items-center py-3 px-4 bg-gray-800 rounded-lg">
                  <span className="text-sm text-gray-400">
                    {editableItems.length} item(s)
                  </span>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Order Total</div>
                    <div className="text-lg font-bold text-orange-400">
                      ${formatCurrency(totals.total)}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-600">
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-600">
                  <button
                    onClick={() =>
                      navigate(`/projects/${id}`, {
                        state: { initialTab: "Materials" },
                      })
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                  >
                    ← Back
                  </button>

                  {isEditable && (
                    <>
                      <button
                        onClick={handleVoidOrder}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        Void
                      </button>
                      {isEditable && (
                        <button
                          onClick={handleConvertToStockOut}
                          disabled={isSaving || isLoadingStockOut}
                          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50 transition-colors"
                        >
                          {isLoadingStockOut
                            ? "Finding Vendors..."
                            : "Convert to Stock-Out"}
                        </button>
                      )}
                      <button
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                      <button
                        onClick={handleSendEmail}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        {isSaving ? "Preparing..." : "Send Email"}
                      </button>
                      <button
                        onClick={handleDeliverOrder}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {isSaving ? "Delivering..." : "Mark Delivered"}
                      </button>
                    </>
                  )}

                  {!isEditable && canEditSurcharges && (
                    <button
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  )}

                  <button
                    onClick={handleDownloadPDF}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {isSaving ? "Generating..." : "Download PDF"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showVariancePanel && varianceSummary.hasChanges && isEditable && (
          <div className="w-[30%] min-w-[280px]">
            <div className="bg-gray-700 rounded-xl border border-orange-500/30 shadow-lg sticky top-6 overflow-hidden">
              <div className="px-4 py-3 bg-gray-800 border-b border-gray-600 flex justify-between items-center">
                <h3 className="text-sm font-bold text-orange-400">
                  Variance Summary
                </h3>
                <button
                  onClick={() => setShowVariancePanel(false)}
                  className="text-gray-400 hover:text-white text-lg leading-none"
                >
                  ×
                </button>
              </div>
              <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="p-2 bg-orange-500/10 border border-orange-500/30 rounded text-xs text-orange-300">
                  <div className="font-semibold mb-1">
                    ⚠ What happens on delivery:
                  </div>
                  <ul className="list-disc list-inside space-y-0.5 text-[11px] text-orange-200/80">
                    <li>A variance budget will be created</li>
                    <li>Material & labor deltas will be recorded</li>
                    <li>Original budget remains unchanged</li>
                  </ul>
                </div>

                {/* Material Changes */}
                {varianceSummary.changes.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      Material Changes ({varianceSummary.changes.length})
                    </h4>
                    <div className="space-y-3">
                      {varianceSummary.changes.map((change, idx) => (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg text-xs border ${
                            change.type === "qty_overage" ||
                            change.type === "new_item"
                              ? "bg-red-500/10 border-red-500/30"
                              : "bg-gray-800 border-gray-600"
                          }`}
                        >
                          <div className="font-medium text-gray-200 mb-2">
                            {change.description}
                          </div>

                          {change.type === "spec_change" && (
                            <>
                              <div className="text-orange-400 font-medium mb-2">
                                Size Change — Budget Conversion
                              </div>

                              <div className="grid grid-cols-2 gap-3 text-[11px]">
                                <div className="bg-gray-900/50 p-2 rounded">
                                  <div className="font-medium text-gray-400 mb-1">
                                    Old Spec (Remaining Available)
                                  </div>
                                  <div className="text-gray-300">
                                    {change.originalSpec}
                                  </div>
                                  <div className="text-gray-500">
                                    {change.originalSFPerUnit} {change.uom}/unit
                                  </div>
                                  <div className="mt-1 text-gray-400">
                                    {change.budgetQty} units = {change.budgetSF}{" "}
                                    {change.uom}
                                  </div>
                                  <div className="text-gray-300 font-medium mt-1">
                                    Cost: ${formatCurrency(change.oldSpecCost)}
                                  </div>
                                </div>

                                <div className="bg-gray-900/50 p-2 rounded border border-orange-500/30">
                                  <div className="font-medium text-orange-400 mb-1">
                                    New Spec (Equivalent Budget)
                                  </div>
                                  <div className="text-gray-300">
                                    {change.currentSpec}
                                  </div>
                                  <div className="text-gray-500">
                                    {change.currentSFPerUnit} {change.uom}/unit
                                  </div>
                                  <div className="mt-1 text-gray-400">
                                    {change.equivalentQtyAtNewSize} units ={" "}
                                    {change.equivalentSF} {change.uom}
                                  </div>
                                  <div className="text-gray-300 font-medium mt-1">
                                    Cost: ${formatCurrency(change.newSpecCost)}
                                  </div>
                                </div>
                              </div>

                              <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-[11px]">
                                <div className="font-medium text-blue-400 mb-1">
                                  This Delivery
                                </div>
                                <div className="flex justify-between text-blue-300">
                                  <span>Ordering now:</span>
                                  <span>
                                    {change.deliveredQty} of{" "}
                                    {change.equivalentQtyAtNewSize} units
                                  </span>
                                </div>
                                {change.remainingAfterDelivery > 0 && (
                                  <div className="flex justify-between text-blue-200/70">
                                    <span>Remaining for future orders:</span>
                                    <span>
                                      {change.remainingAfterDelivery} units
                                    </span>
                                  </div>
                                )}
                              </div>

                              {change.exceedsEquivalent && (
                                <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-[11px]">
                                  <div className="font-medium text-amber-400 mb-1">
                                    ⚠ Ordering Beyond Equivalent Budget
                                  </div>
                                  <div className="flex justify-between text-amber-300">
                                    <span>Overage quantity:</span>
                                    <span>{change.overageQty} units</span>
                                  </div>
                                  <div className="flex justify-between text-amber-400 font-medium mt-1">
                                    <span>Overage cost:</span>
                                    <span>
                                      +${formatCurrency(change.overageCost)}
                                    </span>
                                  </div>
                                </div>
                              )}

                              <div
                                className={`text-right font-bold text-lg mt-4 pt-2 border-t border-gray-700 ${
                                  change.delta >= 0
                                    ? "text-red-400"
                                    : "text-green-400"
                                }`}
                              >
                                {change.exceedsEquivalent ? (
                                  <>
                                    <div className="text-sm font-normal text-gray-400 mb-1">
                                      Conversion:{" "}
                                      {change.conversionDelta >= 0 ? "+" : ""}$
                                      {formatCurrency(
                                        Math.abs(change.conversionDelta),
                                      )}
                                      {" + "}
                                      Overage: +$
                                      {formatCurrency(change.overageCost)}
                                    </div>
                                    Net Material Variance:{" "}
                                    {change.delta >= 0 ? "+" : ""}$
                                    {formatCurrency(Math.abs(change.delta))}
                                  </>
                                ) : (
                                  <>
                                    Net Material Variance:{" "}
                                    {change.delta >= 0 ? "+" : ""}$
                                    {formatCurrency(Math.abs(change.delta))}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                          {/* === PRICE CHANGE (no size change) === */}
                          {change.type === "price_change" && (
                            <>
                              <div className="text-orange-400 font-medium">
                                Price change on remaining budget
                              </div>

                              <div className="mt-2 p-2 bg-gray-900/50 rounded text-[11px]">
                                <div className="grid grid-cols-3 gap-2 text-gray-500">
                                  <div>
                                    <div className="font-medium text-gray-400">
                                      Total Budget
                                    </div>
                                    <div>{change.netRequiredQty} units</div>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-400">
                                      Already Ordered
                                    </div>
                                    <div>{change.qtyOrdered} units</div>
                                    <div className="text-[10px] text-gray-600">
                                      @ ${change.originalPrice}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-400">
                                      Remaining
                                    </div>
                                    <div className="text-orange-400">
                                      {change.remainingBudgetQty} units
                                    </div>
                                    <div className="text-[10px] text-orange-400">
                                      @ ${change.currentPrice}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-3 space-y-2 text-[11px]">
                                <div className="grid grid-cols-2 gap-4 text-gray-500">
                                  <div>
                                    <div className="font-medium text-gray-400">
                                      Closeout (Old Price)
                                    </div>
                                    <div>
                                      -{change.remainingBudgetQty} units @ $
                                      {change.originalPrice}
                                    </div>
                                    <div className="text-green-400">
                                      Credit: $
                                      {formatCurrency(
                                        Math.abs(change.closeoutAmount),
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-400">
                                      New Price
                                    </div>
                                    <div>
                                      +{change.remainingBudgetQty} units @ $
                                      {change.currentPrice}
                                    </div>
                                    <div className="text-red-400">
                                      Cost: $
                                      {formatCurrency(change.newPriceAmount)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-2 text-[11px] text-blue-400">
                                Delivering {change.deliveredQty} of{" "}
                                {change.remainingBudgetQty} remaining units
                              </div>

                              <div
                                className={`text-right font-bold text-lg mt-4 ${
                                  change.delta >= 0
                                    ? "text-red-400"
                                    : "text-green-400"
                                }`}
                              >
                                Net: {change.delta >= 0 ? "+" : ""}$
                                {formatCurrency(Math.abs(change.delta))}
                              </div>
                            </>
                          )}

                          {/* === QUANTITY OVERAGE === */}
                          {/* === QUANTITY OVERAGE === */}
                          {change.type === "qty_overage" && (
                            <>
                              <div className="text-red-400 font-medium">
                                ⚠ Exceeds available budget
                              </div>
                              <div className="text-gray-400 mt-1">
                                Budget: {change.netRequiredQty} | Delivered:{" "}
                                {change.qtyOrdered} | Avail:{" "}
                                {change.remainingAvailable}
                              </div>
                              <div className="text-gray-300 mt-1">
                                Ordering: {change.orderedQty} → Overage:{" "}
                                <span className="text-red-400 font-medium">
                                  +{change.overageQty}
                                </span>
                              </div>
                              <div className="text-[10px] text-gray-500 mt-1">
                                {change.spec} • {change.coveragePerUnit} SF/unit
                              </div>

                              {change.otherPending > 0 && (
                                <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-[11px]">
                                  <div className="font-medium text-amber-400 mb-1">
                                    ⚠ Commitment Warning
                                  </div>
                                  <div className="text-amber-300">
                                    Total committed across other orders:{" "}
                                    <span className="font-medium">
                                      {change.totalCommitted}
                                    </span>{" "}
                                    / {change.netRequiredQty} budget
                                  </div>
                                  <div className="text-amber-200/70 text-[10px] mt-1">
                                    {change.otherPending} units pending on other
                                    orders. Additional variances may occur when
                                    those orders deliver.
                                  </div>
                                </div>
                              )}

                              <div className="text-right font-bold text-red-400 text-lg mt-3">
                                +${formatCurrency(change.delta)}
                              </div>
                            </>
                          )}
                          {/* === NEW VARIANCE ITEM === */}
                          {change.type === "new_item" && (
                            <>
                              <div className="text-orange-400 font-medium">
                                New variance item (not in budget)
                              </div>
                              <div className="text-gray-400 mt-1">
                                {change.orderedQty} × {change.spec}
                              </div>

                              <div className="text-right font-bold text-red-400 text-lg mt-3">
                                +${formatCurrency(change.delta)}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Labor Adjustments */}
                {varianceSummary.laborChanges.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      Labor Coverage Adjustments (
                      {varianceSummary.laborChanges.length})
                    </h4>
                    <p className="text-[10px] text-gray-500 mb-3">
                      Material changes affect linked labor coverage.
                    </p>
                    <div className="space-y-2">
                      {varianceSummary.laborChanges.map((labor, idx) => {
                        const rateOverrideKey = labor.laborLineItemId;
                        const deltaOverrideKey = `${labor.materialLineItemId}_${labor.laborLineItemId}`;

                        console.log(labor);

                        const isRateOverridden =
                          laborRateOverrides[rateOverrideKey] !== undefined;
                        const isDeltaOverridden =
                          laborDeltaOverrides[deltaOverrideKey] !== undefined;

                        const displayRate = isRateOverridden
                          ? laborRateOverrides[rateOverrideKey]
                          : labor.unitRate;
                        const displayDelta = isDeltaOverridden
                          ? laborDeltaOverrides[deltaOverrideKey]
                          : labor.coverageDelta;
                        const displayCost = displayDelta * displayRate;

                        return (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg text-xs border border-gray-600 ${
                              labor.included
                                ? "bg-gray-800"
                                : "bg-gray-800/40 opacity-60"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="text-gray-200 font-medium">
                                  [{labor.laborId}] {labor.description}
                                </div>

                                <div className="mt-2 flex items-center gap-2">
                                  <span className="text-gray-500 text-[10px]">
                                    Delta:
                                  </span>
                                  <NumericFormat
                                    value={displayDelta}
                                    onValueChange={(v) => {
                                      const newDelta = v.floatValue ?? 0;
                                      setLaborDeltaOverrides((prev) => {
                                        if (newDelta === labor.coverageDelta) {
                                          const copy = { ...prev };
                                          delete copy[deltaOverrideKey];
                                          return copy;
                                        }
                                        return {
                                          ...prev,
                                          [deltaOverrideKey]: newDelta,
                                        };
                                      });
                                    }}
                                    decimalScale={1}
                                    allowNegative={true}
                                    className={`w-32 px-2 py-1 text-xs rounded border ${
                                      isDeltaOverridden
                                        ? "border-orange-400 bg-orange-900/30"
                                        : "border-gray-500 bg-gray-600"
                                    } text-gray-200`}
                                  />
                                  <span className="text-[10px] text-gray-500">
                                    [{labor.uom}]
                                  </span>
                                  {isDeltaOverridden && (
                                    <button
                                      onClick={() =>
                                        setLaborDeltaOverrides((prev) => {
                                          const copy = { ...prev };
                                          delete copy[deltaOverrideKey];
                                          return copy;
                                        })
                                      }
                                      className="text-orange-400 hover:text-orange-300 text-[9px]"
                                    >
                                      reset
                                    </button>
                                  )}
                                </div>

                                <div className="mt-1 flex items-center gap-2">
                                  <span className="text-gray-500 text-[10px]">
                                    Rate:
                                  </span>
                                  <NumericFormat
                                    value={displayRate}
                                    onValueChange={(v) => {
                                      const newRate = v.floatValue ?? 0;
                                      setLaborRateOverrides((prev) => {
                                        if (newRate === labor.unitRate) {
                                          const copy = { ...prev };
                                          delete copy[rateOverrideKey];
                                          return copy;
                                        }
                                        return {
                                          ...prev,
                                          [rateOverrideKey]: newRate,
                                        };
                                      });
                                    }}
                                    decimalScale={3}
                                    prefix="$"
                                    allowNegative={false}
                                    className={`w-28 px-2 py-1 text-xs rounded border ${
                                      isRateOverridden
                                        ? "border-orange-400 bg-orange-900/30"
                                        : "border-gray-500 bg-gray-600"
                                    } text-gray-200`}
                                  />
                                  {isRateOverridden && (
                                    <button
                                      onClick={() =>
                                        setLaborRateOverrides((prev) => {
                                          const copy = { ...prev };
                                          delete copy[rateOverrideKey];
                                          return copy;
                                        })
                                      }
                                      className="text-orange-400 hover:text-orange-300 text-[9px]"
                                    >
                                      reset
                                    </button>
                                  )}
                                </div>
                              </div>

                              <input
                                type="checkbox"
                                checked={labor.included}
                                onChange={(e) =>
                                  handleLaborToggle(
                                    labor.materialLineItemId,
                                    e.target.checked,
                                  )
                                }
                                className="h-4 w-4 text-orange-400 border-gray-500 rounded mt-1"
                              />
                            </div>

                            <div className="text-right font-bold text-lg mt-3">
                              <span
                                className={
                                  displayCost >= 0
                                    ? "text-red-400"
                                    : "text-green-400"
                                }
                              >
                                {displayCost >= 0 ? "+" : ""}$
                                {formatCurrency(Math.abs(displayCost))}
                              </span>
                              {!labor.included && (
                                <span className="text-[10px] text-gray-500 ml-2">
                                  (excluded)
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Variance Reason */}
                {/* Variance Reason */}
                <div className="pt-2 border-t border-gray-600">
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                    Variance Reason <span className="text-red-400">*</span>
                  </label>

                  <div className="flex flex-col gap-2">
                    <select
                      value={
                        [
                          "Site Condition Change",
                          "Foreman Field Decision",
                          "Estimation Error",
                          "Design / Engineering Change",
                          "Other",
                        ].includes(formData.varianceReasonCategory)
                          ? formData.varianceReasonCategory
                          : formData.varianceReasonCategory
                            ? "Other"
                            : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        handleFormChange("varianceReasonCategory", val);
                        if (val !== "Other" && val !== "") {
                          // Pre-fill the free text with the category label as a starting point
                          handleFormChange(
                            "varianceReason",
                            val === "N/A" ? "N/A — Reason does not apply" : "",
                          );
                        }
                      }}
                      className={`w-full rounded border bg-gray-600 text-gray-200 text-xs px-3 py-1.5 ${
                        !formData.varianceReasonCategory
                          ? "border-red-500/50 focus:border-red-500"
                          : "border-gray-500 focus:border-orange-500"
                      }`}
                    >
                      <option value="">— Select reason category —</option>
                      <option value="Site Condition Change">
                        Site Condition Change
                      </option>

                      <option value="Foreman Field Decision">
                        Foreman Field Decision
                      </option>
                      <option value="Estimation Error">Estimation Error</option>
                      <option value="Design / Engineering Change">
                        Design / Engineering Change
                      </option>

                      <option value="Other">Other (specify)...</option>
                    </select>

                    {/* Free text — always shown so user can add detail */}
                    {formData.varianceReasonCategory &&
                      formData.varianceReasonCategory !== "N/A" && (
                        <textarea
                          value={formData.varianceReason}
                          onChange={(e) =>
                            handleFormChange("varianceReason", e.target.value)
                          }
                          placeholder={
                            formData.varianceReasonCategory === "Other"
                              ? "Describe the reason..."
                              : "Add additional detail (optional)..."
                          }
                          rows={2}
                          className="w-full px-3 py-2 text-xs rounded border bg-gray-600 text-gray-200 border-gray-500 focus:border-orange-500"
                        />
                      )}
                  </div>
                </div>

                {/* Variance Impact */}
                <div className="pt-3 border-t border-gray-600">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Variance Impact
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Material Delta:</span>
                      <span
                        className={`font-bold text-lg ${
                          varianceSummary.totalMaterialDelta >= 0
                            ? "text-red-400"
                            : "text-green-400"
                        }`}
                      >
                        {varianceSummary.totalMaterialDelta >= 0 ? "+" : ""}$
                        {formatCurrency(
                          Math.abs(varianceSummary.totalMaterialDelta),
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Labor Delta:</span>
                      <span
                        className={`font-bold text-lg ${
                          varianceSummary.totalLaborDelta >= 0
                            ? "text-red-400"
                            : "text-green-400"
                        }`}
                      >
                        {varianceSummary.totalLaborDelta >= 0 ? "+" : ""}$
                        {formatCurrency(
                          Math.abs(varianceSummary.totalLaborDelta),
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-gray-700">
                      <span className="text-gray-200 font-semibold">
                        Total Variance:
                      </span>
                      <span
                        className={`font-bold text-xl ${
                          varianceSummary.totalDelta >= 0
                            ? "text-red-400"
                            : "text-green-400"
                        }`}
                      >
                        {varianceSummary.totalDelta >= 0 ? "+" : ""}$
                        {formatCurrency(Math.abs(varianceSummary.totalDelta))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {showEmailForm && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-lg border border-gray-600">
              <h2 className="text-xl font-semibold text-orange-400 mb-4">
                Send Material Order
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    From
                  </label>
                  <input
                    type="email"
                    value={userEmail}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    To
                  </label>
                  <input
                    type="text"
                    value={emailForm.to}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, to: e.target.value })
                    }
                    placeholder="recipient@example.com, another@example.com"
                    className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={emailForm.subject}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, subject: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Body
                  </label>
                  <textarea
                    value={emailForm.body}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, body: e.target.value })
                    }
                    rows="5"
                    className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Signature
                  </label>
                  <textarea
                    value={emailForm.signature}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, signature: e.target.value })
                    }
                    rows="2"
                    className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailForm(false);
                      setVendorPdfBuffer(null);
                      setSitePdfBuffer(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailSubmit}
                    className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-500 rounded-md hover:bg-orange-600 transition-colors"
                    disabled={!vendorPdfBuffer}
                  >
                    Send Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Foreman Size Changes Modal */}
        {showForemanChangesModal && foremanSizeChanges.length > 0 && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50">
            <div className="bg-gray-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-orange-500">
              {/* Header */}
              <div className="px-6 py-4 bg-gradient-to-r from-orange-600 to-orange-500">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">📋</span>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        Foreman Size Changes
                      </h2>
                      <p className="text-orange-100 text-sm">
                        Review changes made by foreman before delivery
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowForemanChangesModal(false)}
                    className="text-white/80 hover:text-white text-2xl leading-none p-1"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[55vh] space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="text-yellow-300 font-medium">
                        The foreman changed material sizes on this order
                      </p>
                      <p className="text-yellow-200/70 text-sm mt-1">
                        This may require updating labor items (e.g., stud
                        lengths) to match. Review each change and decide whether
                        to accept the variance.
                      </p>
                    </div>
                  </div>
                </div>

                {foremanSizeChanges.map((change, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-800 rounded-lg border border-gray-600 overflow-hidden"
                  >
                    <div className="px-4 py-3 bg-gray-750 border-b border-gray-600">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-200">
                          {change.conditionName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {change.materialCode}
                        </span>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex items-center gap-4 mb-3">
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 mb-1">
                            Original Size
                          </div>
                          <div className="px-3 py-2 bg-gray-700 rounded text-gray-300 font-mono">
                            {change.originalSize}
                          </div>
                        </div>
                        <div className="text-gray-500 text-xl">→</div>
                        <div className="flex-1">
                          <div className="text-xs text-orange-400 mb-1">
                            New Size
                          </div>
                          <div className="px-3 py-2 bg-orange-500/20 border border-orange-500/50 rounded text-orange-300 font-mono font-medium">
                            {change.newSize}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">
                          Quantity:{" "}
                          <span className="text-white font-medium">
                            {change.quantity}
                          </span>
                        </span>
                        {change.laborId && (
                          <span className="text-blue-400">
                            Linked Labor: {change.laborId}
                          </span>
                        )}
                      </div>

                      {change.laborId && (
                        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                          <div className="flex items-start gap-2">
                            <span className="text-blue-400">💡</span>
                            <p className="text-blue-300 text-sm">
                              Consider updating stud lengths for{" "}
                              <strong>{change.laborId}</strong> labor items in{" "}
                              <strong>{change.conditionName}</strong> to match
                              the new material size.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-800 border-t border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {foremanSizeChanges.length} size change(s) •{" "}
                    {foremanSizeChanges.filter((c) => c.laborId).length} with
                    linked labor
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowForemanChangesModal(false);
                        setShowVariancePanel(true);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      Review Variances
                    </button>
                    <button
                      onClick={() => setShowForemanChangesModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-600 rounded-lg hover:bg-gray-500 transition-colors"
                    >
                      Acknowledge & Continue
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {!showVariancePanel && varianceSummary.hasChanges && isEditable && (
          <button
            onClick={() => setShowVariancePanel(true)}
            className="fixed right-0 top-1/2 -translate-y-1/2 bg-orange-500 text-white px-1.5 py-3 rounded-l-lg shadow-lg hover:bg-orange-600 transition-colors z-10 text-xs font-medium"
            style={{ writingMode: "vertical-rl" }}
          >
            Variance ({varianceSummary.changes.length})
          </button>
        )}
      </div>
      {showStockOutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-700 rounded-lg border border-gray-600 shadow-2xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-white mb-2">
              Convert to Stock-Out Order
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              This will void the current order and create a new stock-out order
              with the selected vendor. The original vendor's pending quantities
              will be released and re-reserved under the new vendor.
            </p>

            {/* Vendor list */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Select New Vendor
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {stockOutVendors.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    No alternate vendors found for these items.
                  </p>
                ) : (
                  stockOutVendors.map((v) => {
                    const isFullCoverage = v.matchedCount === v.totalItems;
                    const isSelected = stockOutSelectedVendor === v.vendorId;
                    return (
                      <div
                        key={v.vendorId}
                        onClick={() =>
                          isFullCoverage &&
                          setStockOutSelectedVendor(v.vendorId)
                        }
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? "border-orange-500 bg-orange-500/10"
                            : isFullCoverage
                              ? "border-gray-600 bg-gray-800 hover:border-gray-500"
                              : "border-gray-700 bg-gray-800/50 opacity-50 cursor-not-allowed"
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-white font-medium">
                              {v.vendorName}
                            </span>
                            <span
                              className={`ml-2 text-xs ${isFullCoverage ? "text-green-400" : "text-red-400"}`}
                            >
                              {v.matchedCount}/{v.totalItems} items
                            </span>
                          </div>
                          {isSelected && (
                            <span className="text-orange-400 text-sm">
                              ✓ Selected
                            </span>
                          )}
                        </div>
                        {!isFullCoverage && (
                          <p className="text-xs text-red-400 mt-1">
                            Missing {v.totalItems - v.matchedCount} item(s) —
                            cannot select
                          </p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Missing items warning */}
            {stockOutMissing.length > 0 && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs text-red-400 font-medium mb-1">
                  Items without alternate vendor pricing:
                </p>
                <ul className="text-xs text-red-300 list-disc list-inside max-h-24 overflow-y-auto">
                  {stockOutMissing.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Reason */}
            {/* Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Reason for Stock-Out *
              </label>
              <div className="flex flex-col gap-2">
                <select
                  value={stockOutReasonCategory}
                  onChange={(e) => {
                    const val = e.target.value;
                    setStockOutReasonCategory(val);
                    if (val !== "Other") setStockOutReasonText("");
                  }}
                  className="w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 sm:text-sm px-3 py-1.5"
                >
                  <option value="">— Select reason category —</option>
                  <option value="Vendor Cannot Fulfil Order">
                    Vendor Cannot Fulfil Order
                  </option>
                  <option value="Vendor Out of Stock">
                    Vendor Out of Stock
                  </option>
                  <option value="Better Pricing Available">
                    Better Pricing Available
                  </option>
                  <option value="Lead Time Too Long">Lead Time Too Long</option>
                  <option value="Site Condition Change">
                    Site Condition Change
                  </option>
                  <option value="Foreman Field Decision">
                    Foreman Field Decision
                  </option>
                  <option value="Estimation Error">Estimation Error</option>
                  <option value="Design / Engineering Change">
                    Design / Engineering Change
                  </option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowStockOutModal(false)}
                className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteStockOutConversion}
                disabled={!stockOutSelectedVendor || isSaving}
                className="px-4 py-2 text-sm text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {isSaving ? "Converting..." : "Convert Order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
